// path: src/App.js

import React, { Component } from "react";
import "./App.css";
import Profile from "./Profile";
import Friends from "./Friends";
import FriendProfile from "./FriendProfile";
import Leaderboards from "./Leaderboards";
import characters from "./Characters.json";
import Row from "./Components/row/Row.js";
import BottomNav from "./Components/BottomBar";
import AppFooter from "./Components/AppFooter";
import OverlayTopBar from "./Components/OverlayTopBar";
import { auth } from "./firebase";
import Home from "./Home";
import Help from "./Help";
import Legal from "./Legal";
import StoryMode from "./StoryMode";
import EndlessMode from "./EndlessMode";
import Collection from "./Collection";
import Achievements from "./Achievements";
import Cosmetics from "./Cosmetics";
import Recap from "./Recap";
import { getCardImageSrc } from "./utils/cardImages";
import {
  migrateCollectionIds,
  migrateLegacySpecialId,
} from "./utils/cardCatalog";
import { isValidGid } from "./utils/gid";
import StoryLevelIntro from "./Components/story/StoryLevelIntro";
import { STORY_LEVELS, getStoryLevelById, getStoryMainLevelCount } from "./data/storyLevels";
import { getStoryLevelPackReward, getStoryLevelBoostBundleReward } from "./data/storyLevelRewards";
import { completeGoogleRedirectSignIn } from "./firebaseUtils";
import { getCapacitor, isNativeApp } from "./utils/nativePlatform";

import {
  clearBaseGameCompleted,
  markBaseGameCompleted,
  setMaxUnlockedLevel,
} from "./utils/progression";

import {
  completeStoryLevel,
  createEmptyStoryProgress,
  buildAdminUnlockAllStoryProgress,
  getStoryUnlockToastMessages,
  isEndlessHighScoreUnlocked,
  isStoreUnlocked,
  isStoryBoostFeatureUnlocked,
  loadStoryProgress,
  mergeStoryProgress,
  migrateLegacyProgressionIfNeeded,
  normalizeStoryProgress,
  saveStoryProgress,
  isStoryHardModeActive,
  setStoryHardModeEnabled,
} from "./utils/storyProgress";

import {
  applyStoryHardMode,
  buildEndlessRunConfig,
  buildStoryRunConfig,
  getEndlessLevelThreshold,
  getStoryClearTarget,
  getStoryCompletionThreshold,
  isEndlessRun,
  isStoryRun,
  normalizeRunConfig,
} from "./utils/runConfig";

import {
  ANXIOUS_WARN_MS,
  getAnxiousScrambleDelayMs,
  isAnxiousScrambleBlocked,
} from "./utils/anxiousMode";
import { getActiveModifierHelpItems } from "./utils/modifierHelp";

import {
  applyTwistedRotations,
  shuffleCardsChangingAllPositions,
} from "./utils/boardShuffle";

import { isUsernameAllowed } from "./utils/usernameBlocklist";
import { signOut } from "firebase/auth";

import {
  getUserHighScore,
  setUserHighScore,
  getUserCollection,
  setUserCollection,
  getUserFoilCollection,
  setUserFoilCollection,
  getUserProfile,
  setUserProfile,
  getUserHearts,
  setUserHearts,
  getUserTreats,
  setUserTreats,
  getUserInventory,
  setUserInventory,
  getUserIapEntitlements,
  setUserIapEntitlements,
  getUserStoryProgress,
  setUserStoryProgress,
  getUserEndlessStats,
  setUserEndlessStats,
  getUserCosmetics,
  setUserCosmetics,
  getUserRedemptions,
  setUserRedemptions,
  ensureUserGid,
  redeemCode,
  syncUserPublicProfile,
  loadFriendsSocialState,
  sendFriendRequestByGid,
  acceptFriendRequestById,
  denyFriendRequestById,
  cancelFriendRequestById,
  unfriendByUid,
  getPublicProfile,
  submitLeaderboardScore,
  syncMyLeaderboardBests,
  fetchLeaderboardBoard,
} from "./firebaseUtils";

import {
  createEmptyEndlessStats,
  buildAdminUnlockAllEndlessStats,
  ADMIN_UNLOCK_GAMES_PLAYED,
  ADMIN_UNLOCK_HIGH_SCORE,
  loadEndlessStats,
  mergeEndlessStats,
  normalizeEndlessStats,
  saveEndlessStats,
} from "./utils/achievements";

import {
  buildLeaderboardBoardId,
  buildLeaderboardScoresFromEndlessStats,
} from "./utils/leaderboards";

import {
  buildEndlessScoreModifiersFromRunConfig,
  getEndlessPointsPerClick,
} from "./utils/endlessScore";

import {
  HEART_REFILL_MS,
  MAX_HEARTS,
  applyPassiveHeartRefill,
  formatHeartRefillCountdown,
  formatHeartsMeter,
  getHeartRefillMsRemaining,
  grantHearts,
  mergeHeartStates,
  refillHeartsToMax,
} from "./utils/heartEconomy";

import {
  ADMIN_TREATS_GRANT,
  CORGI_PACK_COST,
  clampCorgiPackGrantCount,
  formatTreats,
  getEndlessTreatsForRun,
  getStoryTreatsForClear,
  getTreatsShopItemById,
  grantTreats,
  mergeTreats,
  spendTreats,
  clampTreats,
} from "./utils/treats";

import {
  FOIL_CHANCE,
  drawWeightedCards,
} from "./utils/cardOdds";

import {
  buildBoardShinyUnlockPatch,
  hasSeenShinyDiscovery,
  markShinyDiscoverySeen,
  SHINY_DISCOVERY_MESSAGE,
  SHINY_DISCOVERY_TITLE,
} from "./utils/shinyDiscovery";

import {
  applyHiddenBoardLootToCards,
  getHiddenLootDiscoveryCopy,
  hasSeenHiddenBoostDiscovery,
  hasSeenHiddenPackDiscovery,
  isHiddenBoostKind,
  isHiddenPackKind,
  markHiddenBoostDiscoverySeen,
  markHiddenPackDiscoverySeen,
} from "./utils/hiddenBoardLoot";

import {
  applyPackDropsToCollection,
  openCorgiPacks,
} from "./utils/cardPacks";

import {
  getRewardedHeartAdStatus,
  watchRewardedAdForHeart,
} from "./utils/heartMonetization";
import { shouldUseRewardedAdPreview } from "./utils/rewardedAdPreview";
import {
  canShowInterstitial,
  ensureInterstitialSessionStarted,
  isNaturalBreakTransition,
  recordInterstitialShown,
} from "./utils/periodicInterstitialAds";
import {
  applyEarnedRewardDouble,
  buildEarnedRewardOffers,
  getRewardDoubleAdLabel,
  getRewardedDoubleAdStatus,
  watchRewardedAdForDouble,
} from "./utils/rewardedEarnedRewards";

import {
  autoEquipAvatarFrameIfEmpty,
  autoEquipNameBadgeIfEmpty,
  autoEquipTitleIfEmpty,
  COSMETIC_TYPES,
  createEmptyCosmeticsState,
  equipCosmetic,
  getAvatarCosmeticIdForCard,
  getCosmeticById,
  getEquippedCosmetics,
  grantCosmeticsForCompletedStoryLevels,
  grantCosmeticsForStoryLevel,
  grantOpenBetaTitle,
  loadCosmeticsState,
  mergeCosmeticsState,
  normalizeCosmeticsState,
  OPEN_BETA_NAME_BADGE_ID,
  OPEN_BETA_TITLE_ID,
  saveCosmeticsState,
  syncCollectionCosmetics,
  unequipCosmeticType,
  unlockAllGrantableCosmetics,
} from "./utils/cosmetics";

import HeartEmptyOverlay from "./Components/HeartEmptyOverlay";
import {
  loadReviewPromptState,
  saveReviewPromptState,
  shouldOfferReviewPrompt,
} from "./utils/reviewPrompt";
import NoticePrompt from "./Components/NoticePrompt";
import ReviewPromptModal from "./Components/ReviewPromptModal";
import RewardedAdPreviewModal from "./Components/RewardedAdPreviewModal";
import InterstitialAdPreviewModal from "./Components/InterstitialAdPreviewModal";
import ModifierHelpOverlay from "./Components/ModifierHelpOverlay";
import PackOpenPrompt from "./Components/PackOpenPrompt";
import Store from "./Store";
import StorePackRevealModal from "./Components/StorePackRevealModal";
import GameBoostBar from "./Components/game/GameBoostBar";
import {
  createEmptyInventory,
  getCorgiPackCount,
  grantCorgiPacks,
  grantInventoryItem,
  listOwnedInventoryEntries,
  mergeInventory,
  normalizeInventory,
  readGuestInventory,
  saveGuestInventory,
  spendCorgiPacks,
  spendInventoryItem,
} from "./utils/inventory";
import {
  ADMIN_BOOST_GRANT_EACH,
  BOOST_CATALOG,
  BOOST_ITEM_IDS,
  createEmptyRunBoostState,
  formatBoostBundleLabel,
  getArmedBoostStatusMessage,
  getBoostActivationBlockReason,
  grantAllBoostItems,
  grantBoostBundle,
  listBoostBarEntries,
  pickRevealTargetId,
} from "./utils/boosts";
import { purchaseStoreProduct, syncNativeBillingUser } from "./utils/iapStore";
import {
  getHeartRefillProductId,
  getHeartSingleProductId,
  getHeartTenProductId,
  getIapProductById,
  getNoAdsProductId,
} from "./utils/iapCatalog";
import {
  createEmptyIapEntitlements,
  grantNoAdsEntitlement,
  loadIapEntitlements,
  mergeIapEntitlements,
  normalizeIapEntitlements,
  saveIapEntitlements,
} from "./utils/iapEntitlements";
import {
  applyRedemptionGrant,
  createEmptyRedemptionState,
  markRedemptionClaimed,
  normalizeRedemptionState,
} from "./utils/redemptionCodes";
import { canAccessAdminPanel } from "./utils/adminAccess";
import {
  notifyScreenshotTaken,
  subscribeToScreenshots,
} from "./utils/screenshotGuard";

/** Master switch — still requires an allowlisted signed-in email or Firebase UID. */
const ENABLE_ADMIN_PANEL = true;

const LS_USERNAME_KEY = "mmu_username_v1";
const LS_PROFILE_IMAGE_KEY = "mmu_profile_image_id_v1";
const LS_GAMES_PLAYED_KEY = "mmu_games_played_v1";
const LS_HEARTS_KEY = "mmu_hearts_v1";
const LS_HEART_LAST_REFILL_AT_KEY = "mmu_heart_last_refill_at_v1";
const LS_COLLECTION_KEY = "mmu_collection_unlocked_v1";
const LS_FOIL_COLLECTION_KEY = "mmu_collection_foil_unlocked_v1";
const LS_TREATS_KEY = "mmu_treats_v1";
const HEART_REFRESH_CHECK_MS = 60 * 1000;
const RUN_TIMER_TICK_MS = 1000;
const BLITZ_DURATION_MS = 2 * 60 * 1000;
const SHY_CORGIS_FLIP_LOCK_MS = 800;
const MAX_ENDLESS_LEVEL = 999;
const ENDLESS_CARDS_PER_LEVEL = 16;
const ENDLESS_POINTS_PER_LEVEL = 16;
const UNLOCK_TOAST_MS = 3200;

const normalizeId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

const formatRunDuration = (ms = 0) => {
  const totalSeconds = Math.max(0, Math.floor(Number(ms) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

class App extends Component {
  highScoreSaveTimer = null;
  collectionSaveTimer = null;
  foilCollectionSaveTimer = null;
  heartSaveTimer = null;
  treatSaveTimer = null;
  endlessStatsSaveTimer = null;
  cosmeticsSaveTimer = null;
  publicProfileSaveTimer = null;
  levelIntroTimer = null;
  heartRefreshTimer = null;
  runTimerInterval = null;
  shyCorgisFlipLockTimer = null;
  unlockToastTimer = null;
  anxiousTimer = null;
  anxiousWarnTimer = null;
  adminAutoCompleteTimer = null;
  adminAutoCompleteSession = null;
  unsubscribeAuth = null;

  // Define levels
  levels = [
    { cards: 4, threshold: 4 },
    { cards: 4, threshold: 8 },
    { cards: 4, threshold: 12 },

    { cards: 9, threshold: 21 },
    { cards: 9, threshold: 30 },
    { cards: 9, threshold: 39 },

    { cards: 16, threshold: 55 },
    { cards: 16, threshold: 71 },
    { cards: 16, threshold: 87 },
    { cards: 16, threshold: 103 },
  ];

  getActiveRunConfig = () => {
    return this.state.runConfig || buildEndlessRunConfig({ boardSize: 4 });
  };

  getLevelConfig = (levelIndex) => {
    const runConfig = this.getActiveRunConfig();

    if (isStoryRun(runConfig)) {
      return {
        cards: runConfig.boardSize,
        threshold: getStoryCompletionThreshold(runConfig),
      };
    }

    if (isEndlessRun(runConfig)) {
      return {
        cards: runConfig.boardSize,
        threshold: getEndlessLevelThreshold(runConfig.boardSize, levelIndex),
      };
    }

    // Legacy ladder fallback
    if (this.levels[levelIndex]) {
      return this.levels[levelIndex];
    }

    const finalMainLevel = this.levels[this.levels.length - 1];
    const endlessLevelsCompleted = levelIndex - (this.levels.length - 1);

    return {
      cards: ENDLESS_CARDS_PER_LEVEL,
      threshold:
        finalMainLevel.threshold +
        endlessLevelsCompleted * ENDLESS_POINTS_PER_LEVEL,
    };
  };

  isFinalMainLevelCompleteChoice = () => {
    const runConfig = this.getActiveRunConfig();

    if (isStoryRun(runConfig) || isEndlessRun(runConfig)) {
      return false;
    }

    return (
      this.state.currentLevel === this.levels.length - 1 &&
      this.state.pendingNextLevel === this.levels.length
    );
  };

  persistStoryProgress = (progress) => {
    const normalized = saveStoryProgress(progress);

    this.setState({ storyProgress: normalized });

    if (this.state.isAuthenticated && this.state.user?.uid) {
      setUserStoryProgress(this.state.user.uid, normalized).catch((e) =>
        console.error("Failed to save story progress:", e)
      );
    }

    return normalized;
  };

  setStoryHardMode = (enabled) => {
    this.persistStoryProgress(
      setStoryHardModeEnabled(enabled, this.state.storyProgress)
    );
  };

  constructor(props) {
    super(props);
    this.state = {
      isAuthenticated: false,
      user: null,

      screen: "home",
      screenHistory: [],
      twistedMode: false,
      shyCorgisMode: false,
      anxiousMode: false,
      anxiousWarning: false,
      shyCorgisFlipLocked: false,
      hardcoreMode: false,
      shyHiddenCardIds: [],

      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      showHeartEmptyOverlay: false,
      showRewardedAdPreview: false,
      rewardedAdPreviewLabel: "+1 Heart",
      pendingRewardedAd: null,
      showInterstitialAdPreview: false,
      earnedRewardOffers: [],
      doubledEarnedRewardIds: [],
      earnedRewardDoubleBusy: "",
      earnedRewardDoubleError: "",
      heartRewardBusy: "",
      heartRewardError: "",
      storeBusyProductId: "",
      storeError: "",
      storeRedeemBusy: false,
      storeRedeemMessage: "",
      storeInitialTab: "featured",
      legalDocId: "privacy",
      purchaseNotice: null,
      showReviewPrompt: false,
      storePackBusy: false,
      storePackResult: null,
      pendingNextLevel: null,
      pendingWrongCardId: null,

      cards: this.applyFoilToCards(this.getNewCards(this.levels[0].cards)),
      score: 0,
      endlessProgressClicks: 0,
      topScore: 0,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],
      hearts: MAX_HEARTS,
      heartLastRefillAt: Date.now(),
      treats: 0,
      inventory: createEmptyInventory(),
      packOffer: null,
      iapEntitlements: createEmptyIapEntitlements(),
      redemptions: createEmptyRedemptionState(),

      collectionUnlocked: [],
      collectionFoilUnlocked: [],
      endlessStats: createEmptyEndlessStats(),
      cosmetics: createEmptyCosmeticsState(),
      currentPerfectStreak: 0,
      runHeartsSpent: 0,

      profileImageId: null,
      collectionPickProfile: false,
      username: "",
      gamesPlayed: 0,
      gid: null,
      gidStatus: "idle",
      gidError: "",
      friends: [],
      incomingFriendRequests: [],
      outgoingFriendRequests: [],
      friendsLoading: false,
      friendsError: "",
      friendsActionMessage: "",
      friendsActionError: "",
      friendsBusyActionKey: "",
      viewingFriendUid: null,
      viewingFriendProfile: null,
      viewingFriendLoading: false,
      viewingFriendError: "",
      leaderboardCategoryId: "endless_level",
      leaderboardBoardSize: 4,
      leaderboardEntries: [],
      leaderboardMyEntry: null,
      leaderboardLoading: false,
      leaderboardError: "",

      runCorrectClicks: 0,
      runLevelsCompleted: 0,
      runFoilsFound: 0,
      runHiddenBoostsFound: 0,
      runHiddenPacksFound: 0,
      runNewUnlocks: 0,
      runTreatsEarned: 0,
      runBoostBundlesEarned: 0,
      runOutcome: "loss",
      runLevelReached: 1,
      runMode: "normal",
      runConfig: null,
      runStartedAt: null,
      runElapsedMs: 0,
      runFinalElapsedMs: 0,
      runEndReason: "",
      storyUnlockMessage: "",
      storyClearsCompleted: 0,
      showStoryIntro: false,
      storyBoardDealKey: 0,
      storyAlertKind: "",
      storyGuideStep: null,
      storyGuideHighlightId: null,
      storyGuideWrongLessonDone: false,
      showGuideHeartDemo: false,
      showShinyDiscovery: false,
      hiddenLootDiscoveryKind: null,
      forgivingMistakeMessage: "",
      unlockToastMessage: "",
      showExitConfirm: false,
      pendingExitDestination: null,
      showResumeOverlay: false,
      runPausedAt: null,
      pauseReason: "",
      runPauseAccumulatedMs: 0,
      ...createEmptyRunBoostState(),

      storyProgress: createEmptyStoryProgress(),
      storyMapPage: "surface",

      adminUnlockAll: false,
      adminAutoCompleting: false,
      adminPanelOpen: false,
    };
  }

  componentDidMount() {
    this.syncGameBodyClass();
    const guestCollection = this.readGuestCollection();
    this.loadGuestProfile();
    this.loadGuestHearts();
    this.loadGuestEndlessStats();
    this.loadGuestIapEntitlements();
    const storyProgress = migrateLegacyProgressionIfNeeded();
    const guestTreats = this.readGuestTreats();
    const guestInventory = readGuestInventory();
    const guestCosmetics = this.syncCosmeticsWithStoryProgress(
      loadCosmeticsState(),
      storyProgress,
      guestCollection.collectionUnlocked
    );
    this.setState(
      {
        storyProgress,
        cosmetics: guestCosmetics,
        collectionUnlocked: guestCollection.collectionUnlocked,
        collectionFoilUnlocked: guestCollection.collectionFoilUnlocked,
        treats: guestTreats,
        inventory: guestInventory,
      },
      () => {
        this.saveTreatState();
        this.saveInventoryState();
      }
    );

    this.heartRefreshTimer = setInterval(() => {
      this.refreshHearts();
    }, HEART_REFRESH_CHECK_MS);

    window.addEventListener("focus", this.handleHeartResume);
    window.addEventListener("pageshow", this.handleHeartResume);
    window.addEventListener("online", this.handleHeartResume);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.wireNativeAppState();
    this.completePendingGoogleRedirect();
    this.unsubscribeScreenshots = subscribeToScreenshots(
      this.handleScreenshotTaken
    );
    ensureInterstitialSessionStarted();

    if (typeof window !== "undefined" && window.screen?.orientation?.lock) {
      window.screen.orientation.lock("portrait").catch(() => {});
    }

    this.unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const [
          savedTopScore,
          savedCollection,
          savedFoilCollection,
          savedProfile,
          savedHearts,
          savedTreats,
          savedStoryProgress,
          savedEndlessStats,
          savedCosmetics,
          savedIapEntitlements,
          savedRedemptions,
          savedInventory,
        ] = await Promise.all([
          getUserHighScore(user.uid),
          getUserCollection(user.uid),
          getUserFoilCollection(user.uid),
          getUserProfile(user.uid),
          getUserHearts(user.uid),
          getUserTreats(user.uid),
          getUserStoryProgress(user.uid),
          getUserEndlessStats(user.uid),
          getUserCosmetics(user.uid),
          getUserIapEntitlements(user.uid),
          getUserRedemptions(user.uid),
          getUserInventory(user.uid),
        ]);

        const nextUsername = this.getSafeUsername(
          savedProfile?.username ||
            this.state.username ||
            user.displayName ||
            this.generateDefaultUsername()
        );

        const localHeartState = this.getRefilledHeartState(
          this.state.hearts,
          this.state.heartLastRefillAt
        );

        const accountHeartState = savedHearts
          ? this.getRefilledHeartState(
              savedHearts.hearts,
              savedHearts.heartLastRefillAt
            )
          : null;

        // Prefer the richer stash so guest progress is not wiped on login.
        const nextHeartState = mergeHeartStates(
          localHeartState,
          accountHeartState
        );

        const accountTreats = savedTreats?.treats;
        const nextTreats = mergeTreats(this.state.treats, accountTreats);
        const nextInventory = mergeInventory(
          this.state.inventory,
          savedInventory
        );

        const localStoryProgress =
          this.state.storyProgress || loadStoryProgress();
        const mergedStoryProgress = mergeStoryProgress(
          localStoryProgress,
          savedStoryProgress
        );
        saveStoryProgress(mergedStoryProgress);

        if (
          !savedStoryProgress ||
          JSON.stringify(normalizeStoryProgress(savedStoryProgress)) !==
            JSON.stringify(mergedStoryProgress)
        ) {
          setUserStoryProgress(user.uid, mergedStoryProgress).catch((e) =>
            console.error("Failed to sync story progress:", e)
          );
        }

        const localEndlessStats =
          this.state.endlessStats || loadEndlessStats();
        const mergedEndlessStats = mergeEndlessStats(
          localEndlessStats,
          savedEndlessStats
        );
        saveEndlessStats(mergedEndlessStats);

        if (
          !savedEndlessStats ||
          JSON.stringify(normalizeEndlessStats(savedEndlessStats)) !==
            JSON.stringify(mergedEndlessStats)
        ) {
          setUserEndlessStats(user.uid, mergedEndlessStats).catch((e) =>
            console.error("Failed to sync endless stats:", e)
          );
        }

        const localCosmetics =
          this.state.cosmetics || loadCosmeticsState();
        const mergedCosmetics = this.syncCosmeticsWithStoryProgress(
          mergeCosmeticsState(
            localCosmetics,
            savedCosmetics || savedProfile?.cosmetics
          ),
          mergedStoryProgress,
          Array.from(
            new Set(
              migrateCollectionIds([
                ...(this.state.collectionUnlocked || []),
                ...(Array.isArray(savedCollection) ? savedCollection : []),
              ])
            )
          )
        );
        saveCosmeticsState(mergedCosmetics);

        if (
          !savedCosmetics ||
          JSON.stringify(normalizeCosmeticsState(savedCosmetics)) !==
            JSON.stringify(mergedCosmetics)
        ) {
          setUserCosmetics(user.uid, mergedCosmetics).catch((e) =>
            console.error("Failed to sync cosmetics:", e)
          );
        }

        const localIapEntitlements =
          this.state.iapEntitlements || loadIapEntitlements();
        const mergedIapEntitlements = mergeIapEntitlements(
          localIapEntitlements,
          savedIapEntitlements
        );
        saveIapEntitlements(mergedIapEntitlements);

        if (
          !savedIapEntitlements ||
          JSON.stringify(normalizeIapEntitlements(savedIapEntitlements)) !==
            JSON.stringify(mergedIapEntitlements)
        ) {
          setUserIapEntitlements(user.uid, mergedIapEntitlements).catch((e) =>
            console.error("Failed to sync store entitlements:", e)
          );
        }

        this.setState(
          (prevState) => ({
            isAuthenticated: true,
            user,
            topScore: Math.max(prevState.topScore, savedTopScore),
            username: nextUsername,
            profileImageId:
              savedProfile?.profileImageId !== null &&
              savedProfile?.profileImageId !== undefined
                ? migrateLegacySpecialId(
                    normalizeId(savedProfile.profileImageId)
                  )
                : prevState.profileImageId,
            gamesPlayed: Math.max(
              prevState.gamesPlayed || 0,
              savedProfile?.gamesPlayed || 0
            ),
            gid: isValidGid(savedProfile?.gid)
              ? Number(savedProfile.gid)
              : prevState.gid,
            gidStatus: isValidGid(savedProfile?.gid)
              ? "ready"
              : "loading",
            gidError: "",
            hearts: nextHeartState.hearts,
            heartLastRefillAt: nextHeartState.heartLastRefillAt,
            treats: nextTreats,
            inventory: nextInventory,
            storyProgress: mergedStoryProgress,
            endlessStats: mergedEndlessStats,
            cosmetics: mergedCosmetics,
            iapEntitlements: mergedIapEntitlements,
            redemptions: normalizeRedemptionState(savedRedemptions),
            collectionUnlocked: migrateCollectionIds([
              ...(prevState.collectionUnlocked || []),
              ...(savedCollection || []),
            ]),
            collectionFoilUnlocked: migrateCollectionIds([
              ...(prevState.collectionFoilUnlocked || []),
              ...(savedFoilCollection || []),
            ]),
          }),
          () => {
            this.saveHeartState();
            this.saveTreatState();
            this.saveInventoryState();
            this.provisionGidAndPublicProfile();
            this.syncNativeBillingAccount(user.uid);
          }
        );
      } else {
        this.syncNativeBillingAccount(null);
        this.setState({
          isAuthenticated: false,
          user: null,
          gid: null,
          gidStatus: "idle",
          gidError: "",
          friends: [],
          incomingFriendRequests: [],
          outgoingFriendRequests: [],
          friendsLoading: false,
          friendsError: "",
          friendsActionMessage: "",
          friendsActionError: "",
          friendsBusyActionKey: "",
          viewingFriendUid: null,
          viewingFriendProfile: null,
          viewingFriendLoading: false,
          viewingFriendError: "",
          redemptions: createEmptyRedemptionState(),
          storeRedeemBusy: false,
          storeRedeemMessage: "",
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.screen !== this.state.screen) {
      this.syncGameBodyClass();
      this.handleInterstitialNaturalBreak(prevState.screen, this.state.screen);
    }

    if (
      prevState.iapEntitlements !== this.state.iapEntitlements &&
      this.isAdsRemoved() &&
      this.state.showInterstitialAdPreview
    ) {
      this.setState({ showInterstitialAdPreview: false });
    }

    if (prevState.profileImageId !== this.state.profileImageId) {
      this.saveGuestProfileImage();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          profileImageId: this.state.profileImageId,
        }).catch((e) => console.error("Failed to save profile image:", e));
      }
    }

    if (prevState.gamesPlayed !== this.state.gamesPlayed) {
      this.saveGuestGamesPlayed();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          gamesPlayed: this.state.gamesPlayed || 0,
        }).catch((e) => console.error("Failed to save games played:", e));
      }
    }

    if (prevState.username !== this.state.username) {
      this.saveGuestUsername();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          username: this.getSafeUsername(this.state.username),
        }).catch((e) => console.error("Failed to save username:", e));
      }
    }

    if (
      prevState.hearts !== this.state.hearts ||
      prevState.heartLastRefillAt !== this.state.heartLastRefillAt
    ) {
      this.saveHeartState();
    }

    if (prevState.currentLevel !== this.state.currentLevel) {
      if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);

      this.setState({ showLevelOverlay: true }, () => {
        this.levelIntroTimer = setTimeout(() => {
          this.setState({ showLevelOverlay: false });
        }, 800);
      });
    }

    if (
      this.state.isAuthenticated &&
      this.state.user?.uid &&
      prevState.topScore !== this.state.topScore
    ) {
      if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);

      this.highScoreSaveTimer = setTimeout(() => {
        setUserHighScore(this.state.user.uid, this.state.topScore).catch((e) =>
          console.error("Failed to save high score:", e)
        );
      }, 400);
    }

    if (prevState.collectionUnlocked !== this.state.collectionUnlocked) {
      this.saveGuestCollection();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);

        this.collectionSaveTimer = setTimeout(() => {
          setUserCollection(
            this.state.user.uid,
            this.state.collectionUnlocked
          ).catch((e) => console.error("Failed to save collection:", e));
        }, 500);
      }
    }

    if (prevState.collectionFoilUnlocked !== this.state.collectionFoilUnlocked) {
      this.saveGuestFoilCollection();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.foilCollectionSaveTimer) {
          clearTimeout(this.foilCollectionSaveTimer);
        }

        this.foilCollectionSaveTimer = setTimeout(() => {
          setUserFoilCollection(
            this.state.user.uid,
            this.state.collectionFoilUnlocked
          ).catch((e) => console.error("Failed to save foil collection:", e));
        }, 500);
      }
    }

    if (prevState.endlessStats !== this.state.endlessStats) {
      this.saveGuestEndlessStats();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.endlessStatsSaveTimer) {
          clearTimeout(this.endlessStatsSaveTimer);
        }

        this.endlessStatsSaveTimer = setTimeout(() => {
          setUserEndlessStats(
            this.state.user.uid,
            this.state.endlessStats
          ).catch((e) => console.error("Failed to save endless stats:", e));
        }, 500);
      }
    }

    if (prevState.cosmetics !== this.state.cosmetics) {
      this.saveGuestCosmetics();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.cosmeticsSaveTimer) {
          clearTimeout(this.cosmeticsSaveTimer);
        }

        this.cosmeticsSaveTimer = setTimeout(() => {
          setUserCosmetics(this.state.user.uid, this.state.cosmetics).catch((e) =>
            console.error("Failed to save cosmetics:", e)
          );
        }, 500);
      }
    }

    if (prevState.iapEntitlements !== this.state.iapEntitlements) {
      saveIapEntitlements(this.state.iapEntitlements);

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.iapEntitlementsSaveTimer) {
          clearTimeout(this.iapEntitlementsSaveTimer);
        }

        this.iapEntitlementsSaveTimer = setTimeout(() => {
          setUserIapEntitlements(
            this.state.user.uid,
            this.state.iapEntitlements
          ).catch((e) => console.error("Failed to save store entitlements:", e));
        }, 500);
      }
    }

    if (prevState.redemptions !== this.state.redemptions) {
      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.redemptionsSaveTimer) {
          clearTimeout(this.redemptionsSaveTimer);
        }

        this.redemptionsSaveTimer = setTimeout(() => {
          setUserRedemptions(this.state.user.uid, this.state.redemptions).catch((e) =>
            console.error("Failed to save redemptions:", e)
          );
        }, 500);
      }
    }

    if (
      this.state.isAuthenticated &&
      isValidGid(this.state.gid) &&
      (prevState.gid !== this.state.gid ||
        prevState.username !== this.state.username ||
        prevState.profileImageId !== this.state.profileImageId ||
        prevState.gamesPlayed !== this.state.gamesPlayed ||
        prevState.topScore !== this.state.topScore ||
        prevState.collectionUnlocked !== this.state.collectionUnlocked ||
        prevState.collectionFoilUnlocked !== this.state.collectionFoilUnlocked ||
        prevState.endlessStats !== this.state.endlessStats ||
        prevState.storyProgress !== this.state.storyProgress ||
        prevState.cosmetics !== this.state.cosmetics)
    ) {
      this.schedulePublicProfileSync();
    }
  }

  componentWillUnmount() {
    if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);
    if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);

    if (this.foilCollectionSaveTimer) {
      clearTimeout(this.foilCollectionSaveTimer);
    }

    if (this.endlessStatsSaveTimer) {
      clearTimeout(this.endlessStatsSaveTimer);
    }

    if (this.iapEntitlementsSaveTimer) {
      clearTimeout(this.iapEntitlementsSaveTimer);
    }

    if (this.redemptionsSaveTimer) {
      clearTimeout(this.redemptionsSaveTimer);
    }

    if (this.cosmeticsSaveTimer) {
      clearTimeout(this.cosmeticsSaveTimer);
    }

    if (this.boostMessageTimer) {
      clearTimeout(this.boostMessageTimer);
    }

    if (this.publicProfileSaveTimer) {
      clearTimeout(this.publicProfileSaveTimer);
    }

    if (this.heartSaveTimer) {
      clearTimeout(this.heartSaveTimer);
    }

    if (this.treatSaveTimer) {
      clearTimeout(this.treatSaveTimer);
    }

    if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);
    if (this.heartRefreshTimer) clearInterval(this.heartRefreshTimer);

    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
    }

    window.removeEventListener("focus", this.handleHeartResume);
    window.removeEventListener("pageshow", this.handleHeartResume);
    window.removeEventListener("online", this.handleHeartResume);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    if (this.shyCorgisFlipLockTimer) {
      clearTimeout(this.shyCorgisFlipLockTimer);
    }

    if (this.unlockToastTimer) {
      clearTimeout(this.unlockToastTimer);
    }

    this.clearAnxiousTimers();

    if (this.adminAutoCompleteTimer) {
      clearTimeout(this.adminAutoCompleteTimer);
      this.adminAutoCompleteTimer = null;
    }
    this.adminAutoCompleteSession = null;

    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }

    if (typeof this.unsubscribeScreenshots === "function") {
      this.unsubscribeScreenshots();
      this.unsubscribeScreenshots = null;
    }

    document.body.classList.remove("gameBoardActive", "recapActive");
  }

  shuffleArray = (array) => {
    let shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  applyFoilToCards = (cards, runConfig = null) => {
    const resolvedRunConfig =
      runConfig || (this.state ? this.getActiveRunConfig() : null);

    // Foil variants are Endless-only drops.
    if (isStoryRun(resolvedRunConfig)) {
      return cards.map((card) => ({
        ...card,
        isFoil: false,
      }));
    }

    return cards.map((card) => ({
      ...card,
      isFoil: Math.random() < FOIL_CHANCE,
    }));
  };

  applyTwistedRotationToCards = (
    cards,
    twistedMode = this.state?.twistedMode,
    twistedCardLimit = null,
    requireVisibleTwist = null
  ) => {
    const resolvedLimit =
      twistedCardLimit === null || twistedCardLimit === undefined
        ? Number(
            this.getActiveRunConfig()?.tutorialBehavior?.twistedCardLimit || 0
          )
        : Number(twistedCardLimit) || 0;

    const shouldRequireVisibleTwist =
      requireVisibleTwist === null
        ? Boolean(twistedMode) && isStoryRun(this.getActiveRunConfig())
        : Boolean(requireVisibleTwist);

    return applyTwistedRotations(cards, {
      twistedMode,
      twistedCardLimit: resolvedLimit,
      requireVisibleTwist: shouldRequireVisibleTwist,
    });
  };

  prepareCardsForBoard = (
    cards,
    twistedMode = this.state?.twistedMode,
    twistedCardLimit = null,
    requireVisibleTwist = null,
    runConfig = null
  ) => {
    const resolvedRunConfig =
      runConfig || (this.state ? this.getActiveRunConfig() : null);

    return this.applyTwistedRotationToCards(
      applyHiddenBoardLootToCards(
        this.applyFoilToCards(cards, resolvedRunConfig),
        { allowHiddenLoot: !isStoryRun(resolvedRunConfig) }
      ),
      twistedMode,
      twistedCardLimit,
      requireVisibleTwist
    );
  };

  /**
   * Apply board cards and unlock any new board Shinies into Collection.
   * First new foil for a player opens the one-time discovery prompt.
   */
  buildBoardCardsStatePatch = (nextCards, extra = {}) => {
    const shiny = buildBoardShinyUnlockPatch({
      cards: nextCards,
      collectionUnlocked: this.state.collectionUnlocked,
      collectionFoilUnlocked: this.state.collectionFoilUnlocked,
      normalizeId,
    });

    const patch = {
      cards: nextCards,
      ...extra,
    };

    if (!shiny.granted) {
      return patch;
    }

    patch.collectionUnlocked = shiny.collectionUnlocked;
    patch.collectionFoilUnlocked = shiny.collectionFoilUnlocked;

    if (shiny.unlockedBaseCount > 0) {
      const baseRunNewUnlocks =
        extra.runNewUnlocks !== undefined
          ? Number(extra.runNewUnlocks) || 0
          : Number(this.state.runNewUnlocks) || 0;
      patch.runNewUnlocks = baseRunNewUnlocks + shiny.unlockedBaseCount;
      patch.cosmetics = syncCollectionCosmetics(
        extra.cosmetics || this.state.cosmetics,
        shiny.collectionUnlocked,
        {
          preferredCardId:
            extra.profileImageId || this.state.profileImageId || null,
        }
      );
    }

    if (
      shiny.newlyGrantedFoilIds.length > 0 &&
      !hasSeenShinyDiscovery()
    ) {
      patch.showShinyDiscovery = true;
    }

    return patch;
  };

  dismissShinyDiscovery = () => {
    markShinyDiscoverySeen();
    this.setState({ showShinyDiscovery: false });
  };

  dismissHiddenLootDiscovery = () => {
    const kind = this.state.hiddenLootDiscoveryKind;
    if (kind === "pack") {
      markHiddenPackDiscoverySeen();
    } else if (kind === "boost") {
      markHiddenBoostDiscoverySeen();
    }
    this.setState({ hiddenLootDiscoveryKind: null });
  };

  getMaxShyHiddenCards = () => {
    const runConfig = this.getActiveRunConfig();
    const configuredMax = runConfig?.tutorialBehavior?.maxHiddenCards;

    if (configuredMax !== null && configuredMax !== undefined) {
      return Math.max(0, Number(configuredMax) || 0);
    }

    return Math.floor((this.state.cards || []).length / 2);
  };

  getShyStartDelayMs = () => {
    const runConfig = this.getActiveRunConfig();
    const configuredDelay = runConfig?.tutorialBehavior?.shyStartDelayMs;

    if (configuredDelay !== null && configuredDelay !== undefined) {
      return Math.max(0, Number(configuredDelay) || 0);
    }

    return null;
  };

  clearShyCorgisFlipLockAfterDelay = () => {
    if (this.shyCorgisFlipLockTimer) {
      clearTimeout(this.shyCorgisFlipLockTimer);
    }

    this.shyCorgisFlipLockTimer = setTimeout(() => {
      this.setState({ shyCorgisFlipLocked: false });
      this.shyCorgisFlipLockTimer = null;
    }, SHY_CORGIS_FLIP_LOCK_MS);
  };

  requestShyCorgiHide = (cardId) => {
  if (!this.state.shyCorgisMode) {
    return false;
  }

  const normalizedCardId = String(cardId);
  const maxHidden = this.getMaxShyHiddenCards();

    if (maxHidden <= 0) {
      return false;
    }

    if (this.state.shyHiddenCardIds.includes(normalizedCardId)) {
      return true;
    }

    if (this.state.shyCorgisFlipLocked) {
      return false;
    }

    if (this.state.shyHiddenCardIds.length >= maxHidden) {
      return false;
    }

  this.setState((prevState) => {
    if (prevState.shyHiddenCardIds.includes(normalizedCardId)) {
      return null;
    }

    if (prevState.shyCorgisFlipLocked) {
      return null;
    }

    if (prevState.shyHiddenCardIds.length >= this.getMaxShyHiddenCards()) {
      return null;
    }

    return {
      shyCorgisFlipLocked: true,
      shyHiddenCardIds: [...prevState.shyHiddenCardIds, normalizedCardId],
    };
  }, this.clearShyCorgisFlipLockAfterDelay);

  return true;
  };

  releaseShyCorgiHide = (cardId) => {
    const normalizedCardId = String(cardId);

    this.setState((prevState) => {
      if (!prevState.shyHiddenCardIds.includes(normalizedCardId)) {
        return null;
      }

      return {
        shyHiddenCardIds: prevState.shyHiddenCardIds.filter(
          (id) => id !== normalizedCardId
        ),
      };
    });
  };

  shuffle = () => {
    const repositioned = shuffleCardsChangingAllPositions(this.state.cards);
    const c = this.applyTwistedRotationToCards(
      repositioned,
      this.state.twistedMode
    );

    this.setState({ cards: c, anxiousWarning: false });
    return c;
  };

  clearAnxiousTimers = () => {
    if (this.anxiousTimer) {
      clearTimeout(this.anxiousTimer);
      this.anxiousTimer = null;
    }
    if (this.anxiousWarnTimer) {
      clearTimeout(this.anxiousWarnTimer);
      this.anxiousWarnTimer = null;
    }
  };

  isAnxiousScrambleBlocked = () => {
    return isAnxiousScrambleBlocked(this.state);
  };

  scheduleAnxiousScramble = () => {
    this.clearAnxiousTimers();

    if (!this.state.anxiousMode || this.state.screen !== "game") {
      this.setState((prev) =>
        prev.anxiousWarning ? { anxiousWarning: false } : null
      );
      return;
    }

    const delayMs = getAnxiousScrambleDelayMs();
    const warnAt = Math.max(0, delayMs - ANXIOUS_WARN_MS);

    this.anxiousWarnTimer = setTimeout(() => {
      if (this.isAnxiousScrambleBlocked()) {
        // Never leave the scramble timer armed without a warn pulse.
        this.scheduleAnxiousScramble();
        return;
      }
      this.setState({ anxiousWarning: true });
    }, warnAt);

    this.anxiousTimer = setTimeout(() => {
      this.performAnxiousScramble();
    }, delayMs);
  };

  performAnxiousScramble = () => {
    if (this.isAnxiousScrambleBlocked()) {
      this.setState({ anxiousWarning: false });
      this.scheduleAnxiousScramble();
      return;
    }

    this.shuffle();
    this.scheduleAnxiousScramble();
  };

  isHighScoreEnabled = () => {
    const runConfig = this.getActiveRunConfig();
    return (
      isEndlessRun(runConfig) &&
      isEndlessHighScoreUnlocked(this.state.storyProgress)
    );
  };

  showUnlockToast = () => {};

  requestLeaveRun = (destination) => {
    if (this.state.screen !== "game") {
      this.performLeaveRun(destination);
      return;
    }

    this.setState({
      showExitConfirm: true,
      pendingExitDestination: destination,
    });
  };

  cancelLeaveRun = () => {
    this.setState({
      showExitConfirm: false,
      pendingExitDestination: null,
    });
  };

  confirmLeaveRun = () => {
    const destination = this.state.pendingExitDestination || "home";

    this.setState(
      {
        showExitConfirm: false,
        pendingExitDestination: null,
      },
      () => this.performLeaveRun(destination)
    );
  };

  buildScreenHistory = (prevScreen, nextScreen, history = []) => {
    let nextHistory = Array.isArray(history) ? [...history] : [];

    // Avoid Back landing on the same screen (e.g. story still on the stack).
    while (nextHistory[nextHistory.length - 1] === nextScreen) {
      nextHistory.pop();
    }

    if (
      prevScreen &&
      nextScreen &&
      prevScreen !== nextScreen &&
      prevScreen !== "recap" &&
      prevScreen !== "game"
    ) {
      nextHistory.push(prevScreen);
    }
    if (nextHistory.length > 24) {
      return nextHistory.slice(-24);
    }
    return nextHistory;
  };

  pushScreen = (nextScreen, extraState = {}) => {
    if (!nextScreen) {
      return;
    }

    this.setState((prev) => ({
      ...extraState,
      screen: nextScreen,
      screenHistory: this.buildScreenHistory(
        prev.screen,
        nextScreen,
        prev.screenHistory
      ),
    }));
  };

  goBack = () => {
    if (this.state.screen === "game") {
      if (this.state.showStoryIntro) {
        this.cancelStoryIntro();
        return;
      }

      this.requestLeaveRun("back");
      return;
    }

    this.navigateBackFromHistory();
  };

  navigateBackFromHistory = () => {
    this.setState((prev) => {
      const history = Array.isArray(prev.screenHistory)
        ? [...prev.screenHistory]
        : [];
      let previous = "home";

      while (history.length > 0) {
        const candidate = history.pop();
        if (candidate && candidate !== "game" && candidate !== "recap") {
          previous = candidate;
          break;
        }
      }

      const nextState = {
        screen: previous,
        screenHistory: previous === "home" ? [] : history,
      };

      if (prev.screen === "friendProfile") {
        nextState.viewingFriendUid = null;
        nextState.viewingFriendProfile = null;
        nextState.viewingFriendLoading = false;
        nextState.viewingFriendError = "";
      }

      return nextState;
    });
  };

  performLeaveRun = (destination) => {
    if (destination === "back") {
      this.resetGameToStart();
      this.navigateBackFromHistory();
      return;
    }

    if (destination === "profile") {
      this.resetGameToStart();
      this.pushScreen("profile");
      return;
    }

    if (destination === "friends") {
      this.resetGameToStart();
      this.pushScreen("friends");
      return;
    }

    if (destination === "leaderboards") {
      this.resetGameToStart();
      this.pushScreen("leaderboards");
      return;
    }

    if (destination === "store") {
      this.resetGameToStart();
      this.pushScreen("store", {
        storeBusyProductId: "",
        storeError: "",
      });
      return;
    }

    if (destination === "story") {
      this.goStoryMode();
      return;
    }

    if (destination === "endless") {
      this.goEndlessMode();
      return;
    }

    this.resetGameToStart();
    this.setState({ screen: "home", screenHistory: [] });
  };

  loadGuestProfile = () => {
    try {
      const rawProfileImageId = localStorage.getItem(LS_PROFILE_IMAGE_KEY);
      const rawGamesPlayed = localStorage.getItem(LS_GAMES_PLAYED_KEY);
      const rawUsername = localStorage.getItem(LS_USERNAME_KEY);

      const parsedProfileImageId = rawProfileImageId
        ? migrateLegacySpecialId(normalizeId(rawProfileImageId))
        : null;

      const parsedGamesPlayed = Number(rawGamesPlayed);

      const username = rawUsername
        ? this.getSafeUsername(rawUsername)
        : this.generateDefaultUsername();

      localStorage.setItem(LS_USERNAME_KEY, username);

      this.setState({
        profileImageId: parsedProfileImageId,
        username,
        gamesPlayed: Number.isFinite(parsedGamesPlayed) ? parsedGamesPlayed : 0,
      });
    } catch {
      // ignore
    }
  };

  saveGuestProfileImage = () => {
    try {
      if (this.state.profileImageId === null || this.state.profileImageId === undefined) {
        localStorage.removeItem(LS_PROFILE_IMAGE_KEY);
        return;
      }

      localStorage.setItem(LS_PROFILE_IMAGE_KEY, String(this.state.profileImageId));
    } catch {
      // ignore
    }
  };

  saveGuestGamesPlayed = () => {
    try {
      localStorage.setItem(LS_GAMES_PLAYED_KEY, String(this.state.gamesPlayed || 0));
    } catch {
      // ignore
    }
  };

  selectProfileImage = (id) => {
    this.setState({
      profileImageId: normalizeId(id),
    });
  };

  readGuestCollection = () => {
    try {
      const raw = localStorage.getItem(LS_COLLECTION_KEY);
      const foilRaw = localStorage.getItem(LS_FOIL_COLLECTION_KEY);

      const parsed = raw ? JSON.parse(raw) : [];
      const foilParsed = foilRaw ? JSON.parse(foilRaw) : [];

      return {
        collectionUnlocked: migrateCollectionIds(parsed),
        collectionFoilUnlocked: migrateCollectionIds(foilParsed),
      };
    } catch {
      return {
        collectionUnlocked: [],
        collectionFoilUnlocked: [],
      };
    }
  };

  loadGuestCollection = () => {
    const next = this.readGuestCollection();
    this.setState(next);
  };

  saveGuestCollection = () => {
    try {
      localStorage.setItem(
        LS_COLLECTION_KEY,
        JSON.stringify(this.state.collectionUnlocked || [])
      );
    } catch {
      // ignore
    }
  };

  saveGuestFoilCollection = () => {
    try {
      localStorage.setItem(
        LS_FOIL_COLLECTION_KEY,
        JSON.stringify(this.state.collectionFoilUnlocked || [])
      );
    } catch {
      // ignore
    }
  };

  loadGuestEndlessStats = () => {
    this.setState({
      endlessStats: loadEndlessStats(),
    });
  };

  loadGuestIapEntitlements = () => {
    this.setState({
      iapEntitlements: loadIapEntitlements(),
    });
  };

  persistIapEntitlements = (nextEntitlements) => {
    const normalized = normalizeIapEntitlements(nextEntitlements);
    saveIapEntitlements(normalized);
    if (this.state.isAuthenticated && this.state.user?.uid) {
      setUserIapEntitlements(this.state.user.uid, normalized).catch((e) =>
        console.error("Failed to save store entitlements:", e)
      );
    }
    return normalized;
  };

  isAdsRemoved = () => Boolean(this.state.iapEntitlements?.noAds);

  handleInterstitialNaturalBreak = (fromScreen, toScreen) => {
    if (!isNaturalBreakTransition(fromScreen, toScreen)) {
      return;
    }

    this.tryShowInterstitialAtNaturalBreak();
  };

  tryShowInterstitialAtNaturalBreak = () => {
    if (
      !canShowInterstitial(this.state, Date.now(), {
        adsRemoved: this.isAdsRemoved(),
      })
    ) {
      return;
    }

    this.showInterstitialAd();
  };

  showInterstitialAd = () => {
    if (
      !canShowInterstitial(this.state, Date.now(), {
        adsRemoved: this.isAdsRemoved(),
      })
    ) {
      return;
    }

    if (shouldUseRewardedAdPreview()) {
      this.setState({ showInterstitialAdPreview: true });
      return;
    }

    this.handleInterstitialAdPreviewComplete();
  };

  handleInterstitialAdPreviewComplete = () => {
    recordInterstitialShown(Date.now());
    this.setState({ showInterstitialAdPreview: false });
  };

  syncNativeBillingAccount = (appUserID = null) => {
    syncNativeBillingUser(appUserID)
      .then((result) => {
        if (result?.noAds && !this.isAdsRemoved()) {
          this.setState({
            iapEntitlements: this.persistIapEntitlements(
              grantNoAdsEntitlement(this.state.iapEntitlements)
            ),
          });
        }
      })
      .catch((error) => {
        console.error("Failed to sync native billing user:", error);
      });
  };

  saveGuestEndlessStats = () => {
    saveEndlessStats(this.state.endlessStats);
  };

  persistEndlessStats = (nextStats) => {
    const previous = normalizeEndlessStats(this.state.endlessStats);
    const normalized = normalizeEndlessStats(nextStats);
    this.setState({ endlessStats: normalized }, () => {
      saveEndlessStats(normalized);
      this.queueLeaderboardSubmissions(previous, normalized);
    });
    return normalized;
  };

  loadGuestCosmetics = () => {
    this.setState({
      cosmetics: this.syncCosmeticsWithStoryProgress(
        loadCosmeticsState(),
        this.state.storyProgress || loadStoryProgress(),
        this.state.collectionUnlocked || []
      ),
    });
  };

  /**
   * Grant Story cosmetics for completed levels, Open Beta title, Collection
   * avatars, and auto-equip a frame/title when the player does not already
   * have one.
   */
  syncCosmeticsWithStoryProgress = (
    cosmetics,
    storyProgress,
    collectionUnlocked,
    endlessStats = null
  ) => {
    const unlockedCards =
      collectionUnlocked || this.state.collectionUnlocked || [];
    const backfill = grantCosmeticsForCompletedStoryLevels(
      cosmetics,
      storyProgress
    );
    const withBeta = grantOpenBetaTitle(backfill.state);
    const withAvatars = syncCollectionCosmetics(
      withBeta.state,
      unlockedCards,
      {
        preferredCardId: this.state.profileImageId,
        storyProgress,
        endlessStats:
          endlessStats != null ? endlessStats : this.state.endlessStats,
      }
    );
    const preferredFrameId =
      backfill.grantedItems.find(
        (item) => item && item.type === COSMETIC_TYPES.AVATAR_FRAME
      )?.id || "frame_journey";
    const preferredTitleId =
      backfill.grantedItems.find(
        (item) => item && item.type === COSMETIC_TYPES.TITLE
      )?.id || OPEN_BETA_TITLE_ID;
    const autoFrame = autoEquipAvatarFrameIfEmpty(
      withAvatars,
      preferredFrameId
    );
    const autoTitle = autoEquipTitleIfEmpty(autoFrame.state, preferredTitleId);
    const autoBadge = autoEquipNameBadgeIfEmpty(
      autoTitle.state,
      OPEN_BETA_NAME_BADGE_ID
    );
    const normalized = normalizeCosmeticsState(autoBadge.state);

    // Keep profileImageId aligned with the equipped Collection avatar.
    const equippedAvatar = getCosmeticById(normalized.equipped.avatarId);
    if (equippedAvatar?.cardId != null) {
      const nextProfileId = normalizeId(equippedAvatar.cardId);
      if (normalizeId(this.state.profileImageId) !== nextProfileId) {
        // Defer to avoid setState during another setState path when possible.
        queueMicrotask(() => {
          if (normalizeId(this.state.profileImageId) !== nextProfileId) {
            this.setState({ profileImageId: nextProfileId });
          }
        });
      }
    }

    saveCosmeticsState(normalized);
    return normalized;
  };

  saveGuestCosmetics = () => {
    saveCosmeticsState(this.state.cosmetics);
  };

  persistCosmetics = (nextCosmetics) => {
    const normalized = normalizeCosmeticsState(nextCosmetics);
    this.setState({ cosmetics: normalized }, () => {
      saveCosmeticsState(normalized);
    });
    return normalized;
  };

  handleEquipCosmetic = (cosmeticId) => {
    const synced = syncCollectionCosmetics(
      this.state.cosmetics,
      this.state.collectionUnlocked || [],
      { preferredCardId: this.state.profileImageId }
    );
    const result = equipCosmetic(synced, cosmeticId);
    if (!result.equipped) {
      return;
    }

    const nextState = { cosmetics: result.state };
    if (result.item?.type === COSMETIC_TYPES.AVATAR && result.item.cardId != null) {
      nextState.profileImageId = normalizeId(result.item.cardId);
    }

    this.setState(nextState, () => {
      saveCosmeticsState(result.state);
    });
  };

  handleUnequipCosmeticType = (type) => {
    const next = unequipCosmeticType(this.state.cosmetics, type);
    this.persistCosmetics(next);
  };

  markEndlessPlayed = () => {
    const current = normalizeEndlessStats(this.state.endlessStats);
    if (current.hasPlayedEndless) {
      return current;
    }

    return this.persistEndlessStats({
      ...current,
      hasPlayedEndless: true,
    });
  };

  recordEndlessPerfectStreak = (nextStreak, boardSize = null) => {
    const streak = Math.max(0, Math.floor(Number(nextStreak) || 0));
    const size = Number(boardSize);
    const current = normalizeEndlessStats(this.state.endlessStats);
    let changed = false;
    const next = {
      ...current,
      bestPerfectStreakByBoardSize: {
        ...current.bestPerfectStreakByBoardSize,
      },
    };

    if (streak > current.bestPerfectStreak) {
      next.bestPerfectStreak = streak;
      changed = true;
    }

    if ([4, 9, 16].includes(size)) {
      const previous = Number(next.bestPerfectStreakByBoardSize[size]) || 0;
      if (streak > previous) {
        next.bestPerfectStreakByBoardSize[size] = streak;
        changed = true;
      }
    }

    if (!changed) {
      return current;
    }

    return this.persistEndlessStats(next);
  };

  recordEndlessBoardLevel = (boardSize, levelReached, runConfig = null) => {
    const size = Number(boardSize);
    const level = Math.max(0, Math.floor(Number(levelReached) || 0));

    if (level <= 0) {
      return normalizeEndlessStats(this.state.endlessStats);
    }

    const current = normalizeEndlessStats(this.state.endlessStats);
    let changed = false;
    const next = {
      ...current,
      bestLevelByBoardSize: { ...current.bestLevelByBoardSize },
      bestBlitzLevelByBoardSize: { ...current.bestBlitzLevelByBoardSize },
      bestLevelWithModifier: { ...current.bestLevelWithModifier },
    };

    if ([4, 9, 16].includes(size)) {
      const previous = Number(next.bestLevelByBoardSize[size]) || 0;
      if (level > previous) {
        next.bestLevelByBoardSize[size] = level;
        changed = true;
      }

      if (runConfig?.blitzMode) {
        const previousBlitz =
          Number(next.bestBlitzLevelByBoardSize[size]) || 0;
        if (level > previousBlitz) {
          next.bestBlitzLevelByBoardSize[size] = level;
          changed = true;
        }
      }
    }

    const modifierFlags = buildEndlessScoreModifiersFromRunConfig(runConfig);

    Object.keys(modifierFlags).forEach((key) => {
      if (!modifierFlags[key]) {
        return;
      }

      const previous = Number(next.bestLevelWithModifier[key]) || 0;
      if (level > previous) {
        next.bestLevelWithModifier[key] = level;
        changed = true;
      }
    });

    if (!changed) {
      return current;
    }

    return this.persistEndlessStats(next);
  };

  recordEndlessRunScore = (boardSize, runScore) => {
    const size = Number(boardSize);
    const score = Math.max(0, Math.floor(Number(runScore) || 0));

    if (
      !this.isHighScoreEnabled() ||
      ![4, 9, 16].includes(size) ||
      score <= 0
    ) {
      return normalizeEndlessStats(this.state.endlessStats);
    }

    const current = normalizeEndlessStats(this.state.endlessStats);
    const previous = Number(current.bestScoreByBoardSize[size]) || 0;

    if (score <= previous) {
      return current;
    }

    return this.persistEndlessStats({
      ...current,
      bestScoreByBoardSize: {
        ...current.bestScoreByBoardSize,
        [size]: score,
      },
    });
  };

  markDontCallItAComeback = () => {
    const current = normalizeEndlessStats(this.state.endlessStats);
    if (current.dontCallItAComeback) {
      return current;
    }

    return this.persistEndlessStats({
      ...current,
      dontCallItAComeback: true,
    });
  };

  findId = (id) => {
    return this.state.clicked.includes(id);
  };

  returnFirstNull = () => {
    return this.state.clicked.indexOf(false);
  };

  insertId = (id, index) => {
    this.setState({
      clicked: this.state.clicked.map((elem, i) => (i === index ? id : elem)),
    });
  };

  getNewCards = (num, runConfig = null, options = {}) => {
    const resolvedRunConfig =
      runConfig || (this.state ? this.getActiveRunConfig() : null);

    return drawWeightedCards(characters, num, {
      allowSpecialVariants: !isStoryRun(resolvedRunConfig),
      excludeIds: options.excludeIds || [],
    });
  };

  getCardIdSet = (cards = []) => {
    return new Set(
      (cards || []).map((card) => String(normalizeId(card.id)))
    );
  };

  cardSetsAreEqual = (leftCards, rightCards) => {
    const left = this.getCardIdSet(leftCards);
    const right = this.getCardIdSet(rightCards);

    if (left.size !== right.size) {
      return false;
    }

    for (const id of left) {
      if (!right.has(id)) {
        return false;
      }
    }

    return true;
  };

  getFreshStoryBoardCards = (boardSize, runConfig = null) => {
    const resolvedRunConfig = runConfig || this.getActiveRunConfig();
    const previousCards = this.state.cards || [];
    const excludeIds = previousCards.map((card) => card.id);
    let attempts = 0;
    let nextCards = this.getNewCards(boardSize, resolvedRunConfig, {
      excludeIds,
    });

    while (
      attempts < 8 &&
      previousCards.length > 0 &&
      this.cardSetsAreEqual(previousCards, nextCards)
    ) {
      nextCards = this.getNewCards(boardSize, resolvedRunConfig);
      attempts += 1;
    }

    return this.prepareCardsForBoard(
      this.shuffleArray(nextCards).slice(0, boardSize),
      Boolean(resolvedRunConfig.twistedMode),
      resolvedRunConfig?.tutorialBehavior?.twistedCardLimit || 0,
      isStoryRun(resolvedRunConfig) && Boolean(resolvedRunConfig.twistedMode),
      resolvedRunConfig
    );
  };

  getStoryBoardPickCount = () => {
    return (this.state.clicked || []).filter((entry) => entry !== false).length;
  };

  resetStoryBoardForNextClear = ({
    message = "",
    alertKind = "clear-complete",
    storyClearsCompleted = null,
  } = {}) => {
    const runConfig = this.getActiveRunConfig();
    const boardSize = runConfig.boardSize || this.state.clicked.length || 4;
    const nextCards = this.getFreshStoryBoardCards(boardSize, runConfig);

    this.setState(
      this.buildBoardCardsStatePatch(nextCards, {
        clicked: Array(boardSize).fill(false),
        shyHiddenCardIds: [],
        shyCorgisFlipLocked: false,
        storyBoardDealKey: (this.state.storyBoardDealKey || 0) + 1,
        storyAlertKind: alertKind,
        forgivingMistakeMessage: message,
        runBoostRevealHighlightId: null,
        ...(storyClearsCompleted == null
          ? {}
          : { storyClearsCompleted }),
      })
    );
  };

  unlockCardIfNeeded = (rawId, isFoil = false) => {
    const id = normalizeId(rawId);

    this.setState((prev) => {
      const regular = prev.collectionUnlocked || [];
      const foil = prev.collectionFoilUnlocked || [];

      const nextState = {};

      if (!regular.includes(id)) {
        nextState.collectionUnlocked = [...regular, id];
        nextState.runNewUnlocks = (prev.runNewUnlocks || 0) + 1;
        nextState.cosmetics = syncCollectionCosmetics(
          prev.cosmetics,
          nextState.collectionUnlocked,
          { preferredCardId: prev.profileImageId || id }
        );
      }

      if (isFoil && !foil.includes(id)) {
        nextState.collectionFoilUnlocked = [...foil, id];
      }

      return Object.keys(nextState).length ? nextState : null;
    }, () => {
      if (this.state.cosmetics) {
        saveCosmeticsState(this.state.cosmetics);
      }
    });
  };

  handleHeartResume = () => {
    this.refreshHearts();
    this.saveHeartState();
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.pauseGameForBackground();
      return;
    }

    if (document.visibilityState === "visible") {
      this.refreshHearts();
      this.saveHeartState();
    }
  };

  wireNativeAppState = () => {
    if (!isNativeApp()) {
      return;
    }

    const cap = getCapacitor();
    const CapApp = cap && cap.Plugins && cap.Plugins.App;
    if (!CapApp || typeof CapApp.addListener !== "function") {
      return;
    }

    try {
      CapApp.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) {
          this.pauseGameForBackground();
        }
      });
    } catch (_) {
      // App plugin optional.
    }
  };

  completePendingGoogleRedirect = () => {
    completeGoogleRedirectSignIn().catch((error) => {
      console.error("Google redirect sign-in failed", error);
    });
  };

  pauseGame = (reason = "background") => {
    if (this.state.screen !== "game") {
      return;
    }

    if (
      this.state.showResumeOverlay ||
      this.state.showStoryIntro ||
      this.state.showLevelCompleteOverlay ||
      this.state.showHeartContinueOverlay ||
      this.state.showHeartEmptyOverlay ||
      this.state.runPausedAt
    ) {
      return;
    }

    if (!this.state.runStartedAt) {
      return;
    }

    this.stopRunTimer();
    this.clearAnxiousTimers();
    this.setState({
      runPausedAt: Date.now(),
      showResumeOverlay: true,
      pauseReason: reason,
      anxiousWarning: false,
    });
  };

  pauseGameForBackground = () => {
    this.pauseGame("background");
  };

  handleScreenshotTaken = () => {
    if (this.state.screen !== "game" || !this.state.runStartedAt) {
      return;
    }

    if (
      this.state.showStoryIntro ||
      this.state.showLevelCompleteOverlay ||
      this.state.showHeartContinueOverlay ||
      this.state.showHeartEmptyOverlay ||
      this.state.showGuideHeartDemo ||
      this.state.showRewardedAdPreview ||
      this.state.showInterstitialAdPreview ||
      this.isGuidedStoryRun()
    ) {
      return;
    }

    // Already showing the photo reaction — ignore repeat taps/events.
    if (
      this.state.showResumeOverlay &&
      this.state.pauseReason === "screenshot"
    ) {
      return;
    }

    const alreadyShy = Boolean(this.state.shyCorgisMode);
    const alreadyAnxious = Boolean(this.state.anxiousMode);
    const currentConfig = this.state.runConfig || this.getActiveRunConfig() || {};
    const priorExcluded = currentConfig.scoreExcludedModifiers || {};
    const nextRunConfig = {
      ...currentConfig,
      shyCorgisMode: true,
      anxiousMode: true,
      // Screenshot-forced modifiers apply gameplay pressure without score bonus.
      scoreExcludedModifiers: {
        shyCorgisMode: Boolean(priorExcluded.shyCorgisMode) || !alreadyShy,
        anxiousMode: Boolean(priorExcluded.anxiousMode) || !alreadyAnxious,
      },
    };

    this.stopRunTimer();
    this.clearAnxiousTimers();

    this.setState((prev) => ({
      shyCorgisMode: true,
      anxiousMode: true,
      anxiousWarning: false,
      runConfig: nextRunConfig,
      runPausedAt: prev.runPausedAt || Date.now(),
      showResumeOverlay: true,
      pauseReason: "screenshot",
    }));
  };

  togglePauseGame = () => {
    if (this.state.runPausedAt || this.state.showResumeOverlay) {
      this.resumeGame();
      return;
    }

    this.pauseGame("manual");
  };

  openModifierHelp = () => {
    this.pauseGame("help");
  };

  resumeGame = () => {
    const pausedAt = this.state.runPausedAt;
    const extra = pausedAt ? Math.max(0, Date.now() - pausedAt) : 0;

    this.setState(
      (prev) => ({
        runPausedAt: null,
        runPauseAccumulatedMs: (prev.runPauseAccumulatedMs || 0) + extra,
        showResumeOverlay: false,
        pauseReason: "",
      }),
      () => {
        if (this.state.screen === "game" && this.state.runStartedAt) {
          this.startRunTimer();
          this.scheduleAnxiousScramble();
        }
      }
    );
  };

  saveUserHearts = () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    if (this.heartSaveTimer) {
      clearTimeout(this.heartSaveTimer);
    }

    this.heartSaveTimer = setTimeout(() => {
      setUserHearts(this.state.user.uid, {
        hearts: this.state.hearts,
        heartLastRefillAt: this.state.heartLastRefillAt,
      }).catch((e) => console.error("Failed to save hearts:", e));
    }, 500);
  };

  saveHeartState = () => {
    this.saveGuestHearts();
    this.saveUserHearts();
  };

  saveUserTreats = () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    if (this.treatSaveTimer) {
      clearTimeout(this.treatSaveTimer);
    }

    this.treatSaveTimer = setTimeout(() => {
      setUserTreats(this.state.user.uid, this.state.treats).catch((e) =>
        console.error("Failed to save treats:", e)
      );
    }, 500);
  };

  saveTreatState = () => {
    this.saveGuestTreats();
    this.saveUserTreats();
  };

  saveUserInventory = () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    if (this.inventorySaveTimer) {
      clearTimeout(this.inventorySaveTimer);
    }

    this.inventorySaveTimer = setTimeout(() => {
      setUserInventory(
        this.state.user.uid,
        normalizeInventory(this.state.inventory)
      ).catch((e) => console.error("Failed to save inventory:", e));
    }, 500);
  };

  saveInventoryState = () => {
    saveGuestInventory(this.state.inventory);
    this.saveUserInventory();
  };

  clearBoostMessageLater = () => {
    if (this.boostMessageTimer) {
      clearTimeout(this.boostMessageTimer);
    }

    this.boostMessageTimer = setTimeout(() => {
      this.setState({ boostMessage: "" });
      this.boostMessageTimer = null;
    }, 3200);
  };

  setBoostMessage = (message = "", { sticky = false } = {}) => {
    if (this.boostMessageTimer) {
      clearTimeout(this.boostMessageTimer);
      this.boostMessageTimer = null;
    }

    this.setState({ boostMessage: String(message || "") }, () => {
      if (message && !sticky) {
        this.clearBoostMessageLater();
      }
    });
  };

  buildArmedBoostPatch = (prevState, patch = {}) => {
    if (this.boostMessageTimer) {
      clearTimeout(this.boostMessageTimer);
      this.boostMessageTimer = null;
    }

    const nextState = { ...prevState, ...patch };
    return {
      ...patch,
      boostMessage: getArmedBoostStatusMessage(nextState),
    };
  };

  activateBoost = (boostItemId = "") => {
    const boost = BOOST_CATALOG.find((entry) => entry.id === boostItemId);
    if (!boost) {
      return;
    }

    // Shield / Freeze: click arms, click again disarms. Inventory spends on fire.
    if (boost.id === BOOST_ITEM_IDS.SECOND_CHANCE) {
      if (this.state.runBoostSecondChanceArmed) {
        this.setState((prev) =>
          this.buildArmedBoostPatch(prev, { runBoostSecondChanceArmed: false })
        );
        return;
      }

      const runConfig = this.getActiveRunConfig();
      const blockReason = getBoostActivationBlockReason({
        boost,
        state: this.state,
        isGuidedRun: this.isGuidedStoryRun(runConfig),
        unusedCardCount: this.getUnusedStoryCardIds().length,
        inventory: this.state.inventory,
      });
      if (blockReason) {
        this.setBoostMessage(blockReason);
        return;
      }

      this.setState((prev) =>
        this.buildArmedBoostPatch(prev, { runBoostSecondChanceArmed: true })
      );
      return;
    }

    if (boost.id === BOOST_ITEM_IDS.FREEZE) {
      if (this.state.runBoostFreezeArmed) {
        this.setState((prev) =>
          this.buildArmedBoostPatch(prev, { runBoostFreezeArmed: false })
        );
        return;
      }

      const runConfig = this.getActiveRunConfig();
      const blockReason = getBoostActivationBlockReason({
        boost,
        state: this.state,
        isGuidedRun: this.isGuidedStoryRun(runConfig),
        unusedCardCount: this.getUnusedStoryCardIds().length,
        inventory: this.state.inventory,
      });
      if (blockReason) {
        this.setBoostMessage(blockReason);
        return;
      }

      this.setState((prev) =>
        this.buildArmedBoostPatch(prev, { runBoostFreezeArmed: true })
      );
      return;
    }

    const runConfig = this.getActiveRunConfig();
    const blockReason = getBoostActivationBlockReason({
      boost,
      state: this.state,
      isGuidedRun: this.isGuidedStoryRun(runConfig),
      unusedCardCount: this.getUnusedStoryCardIds().length,
      inventory: this.state.inventory,
    });

    if (blockReason) {
      this.setBoostMessage(blockReason);
      return;
    }

    const spent = spendInventoryItem(this.state.inventory, boostItemId, 1);
    if (!spent.ok) {
      this.setBoostMessage("None in inventory");
      return;
    }

    if (boost.id === BOOST_ITEM_IDS.REVEAL) {
      const targetId = pickRevealTargetId(
        this.getUnusedStoryCardIds(),
        this.state.shyHiddenCardIds
      );

      if (!targetId) {
        this.setState({ inventory: spent.inventory }, this.saveInventoryState);
        this.setBoostMessage("No unpicked cards left");
        return;
      }

      this.setState(
        {
          inventory: spent.inventory,
          runBoostUsed: {
            ...this.state.runBoostUsed,
            [boost.runKey]: true,
          },
          runBoostRevealHighlightId: targetId,
          boostMessage: "Reveal — tap the glowing card!",
        },
        () => {
          this.saveInventoryState();
          this.clearBoostMessageLater();
        }
      );
    }
  };

  readGuestTreats = () => {
    try {
      const savedTreats = Number(localStorage.getItem(LS_TREATS_KEY));
      return clampTreats(Number.isFinite(savedTreats) ? savedTreats : 0);
    } catch {
      return 0;
    }
  };

  loadGuestTreats = () => {
    this.setState(
      {
        treats: this.readGuestTreats(),
      },
      this.saveTreatState
    );
  };

  saveGuestTreats = () => {
    try {
      localStorage.setItem(LS_TREATS_KEY, String(clampTreats(this.state.treats)));
    } catch {
      // ignore localStorage failures
    }
  };

  getFinishedRunTreatGrant = (outcome, runConfig = null) => {
    const config = runConfig || this.getActiveRunConfig();
    if (isStoryRun(config)) {
      return getStoryTreatsForClear(outcome === "win", config.storyLevelId);
    }
    return getEndlessTreatsForRun({
      score: this.state.score,
      levelsCompleted: this.state.runLevelsCompleted,
      boardSize: config?.boardSize,
      runConfig: config,
    });
  };

  applyTreatGrant = (outcome, runConfig = null) => {
    const amount = this.getFinishedRunTreatGrant(outcome, runConfig);
    return {
      treats: grantTreats(this.state.treats, amount),
      runTreatsEarned: amount,
    };
  };

  getRecapEarnedRewardState = (outcome, treatGrant = {}) => {
    return {
      earnedRewardOffers: buildEarnedRewardOffers({
        outcome,
        treatsEarned: treatGrant.runTreatsEarned || 0,
      }),
      doubledEarnedRewardIds: [],
      earnedRewardDoubleBusy: "",
      earnedRewardDoubleError: "",
    };
  };

  getRefilledHeartState = (
    hearts = MAX_HEARTS,
    heartLastRefillAt = Date.now(),
    now = Date.now()
  ) => {
    return applyPassiveHeartRefill(
      hearts,
      heartLastRefillAt,
      now,
      MAX_HEARTS,
      HEART_REFILL_MS
    );
  };

  loadGuestHearts = () => {
    try {
      const savedHearts = Number(localStorage.getItem(LS_HEARTS_KEY));
      const savedLastRefillAt = Number(
        localStorage.getItem(LS_HEART_LAST_REFILL_AT_KEY)
      );

      const nextHeartState = this.getRefilledHeartState(
        Number.isFinite(savedHearts) ? savedHearts : MAX_HEARTS,
        Number.isFinite(savedLastRefillAt) ? savedLastRefillAt : Date.now()
      );

      this.setState(nextHeartState, this.saveHeartState);
    } catch {
      // ignore localStorage failures
    }
  };

  saveGuestHearts = () => {
    try {
      localStorage.setItem(LS_HEARTS_KEY, String(this.state.hearts));
      localStorage.setItem(
        LS_HEART_LAST_REFILL_AT_KEY,
        String(this.state.heartLastRefillAt)
      );
    } catch {
      // ignore localStorage failures
    }
  };

  refreshHearts = () => {
    let shouldResumeAfterRefill = false;

    this.setState((prevState) => {
      const nextHeartState = this.getRefilledHeartState(
        prevState.hearts,
        prevState.heartLastRefillAt
      );

      if (
        nextHeartState.hearts === prevState.hearts &&
        nextHeartState.heartLastRefillAt === prevState.heartLastRefillAt
      ) {
        return null;
      }

      // Passive refill while the empty-heart funnel is open → resume run.
      if (
        prevState.showHeartEmptyOverlay &&
        prevState.hearts <= 0 &&
        nextHeartState.hearts > 0 &&
        prevState.pendingWrongCardId != null &&
        prevState.screen === "game"
      ) {
        shouldResumeAfterRefill = true;
        return {
          ...nextHeartState,
          showHeartEmptyOverlay: false,
          heartRewardBusy: "",
          heartRewardError: "",
        };
      }

      return nextHeartState;
    }, () => {
      this.saveHeartState();
      if (shouldResumeAfterRefill) {
        this.spendHeartToContinue();
      }
    });
  };

  getNextHeartMinutesText = () => {
    if (this.state.hearts >= MAX_HEARTS) {
      return "Full";
    }

    const msRemaining = getHeartRefillMsRemaining(
      this.state.hearts,
      this.state.heartLastRefillAt
    );
    const label = formatHeartRefillCountdown(msRemaining);
    return label === "Ready" ? "1m" : label;
  };

  openHeartEmptyOverlay = (pendingWrongCardId = null) => {
    this.setState({
      showHeartEmptyOverlay: true,
      showHeartContinueOverlay: false,
      heartRewardBusy: "",
      heartRewardError: "",
      pendingWrongCardId:
        pendingWrongCardId != null
          ? pendingWrongCardId
          : this.state.pendingWrongCardId,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,
      forgivingMistakeMessage: "",
    });
  };

  /**
   * After ad/IAP/passive refill while a wrong-card continue is pending:
   * spend one heart and resume the run.
   */
  resumeRunAfterHeartGrant = () => {
    this.setState(
      {
        showHeartEmptyOverlay: false,
        heartRewardBusy: "",
        heartRewardError: "",
      },
      () => {
        if (this.state.pendingWrongCardId != null && this.state.screen === "game") {
          this.spendHeartToContinue();
          return;
        }

        this.setState({
          showHeartContinueOverlay: false,
          pendingWrongCardId: null,
        });
      }
    );
  };

  handleWatchAdForHeart = async () => {
    if (this.state.heartRewardBusy) {
      return;
    }

    if (this.isAdsRemoved()) {
      this.setState({
        heartRewardError: "Ads are removed on this account.",
      });
      return;
    }

    const adStatus = getRewardedHeartAdStatus(Date.now(), {
      adsRemoved: this.isAdsRemoved(),
    });
    if (!adStatus.available) {
      this.setState({
        heartRewardError:
          "Daily ad limit reached. Wait for a refill or restore hearts.",
      });
      return;
    }

    this.setState({ heartRewardBusy: "ad", heartRewardError: "" });

    if (shouldUseRewardedAdPreview()) {
      this.setState({
        pendingRewardedAd: { type: "heart" },
        rewardedAdPreviewLabel: "+1 Heart",
        showRewardedAdPreview: true,
        heartRewardBusy: "ad",
        heartRewardError: "",
      });
      return;
    }

    await this.completeWatchAdForHeart();
  };

  handleWatchAdForEarnedRewardDouble = async (offerId) => {
    if (this.state.earnedRewardDoubleBusy || this.state.heartRewardBusy) {
      return;
    }

    if (this.isAdsRemoved()) {
      this.setState({
        earnedRewardDoubleError: "Ads are removed on this account.",
      });
      return;
    }

    const offer = (this.state.earnedRewardOffers || []).find(
      (entry) => entry.id === offerId
    );
    if (!offer) {
      return;
    }

    const adStatus = getRewardedDoubleAdStatus(Date.now(), {
      adsRemoved: this.isAdsRemoved(),
    });
    if (!adStatus.available) {
      this.setState({
        earnedRewardDoubleError:
          "Daily ad limit reached. Your base reward is already saved.",
      });
      return;
    }

    if ((this.state.doubledEarnedRewardIds || []).includes(offerId)) {
      return;
    }

    this.setState({
      earnedRewardDoubleBusy: offerId,
      earnedRewardDoubleError: "",
      pendingRewardedAd: { type: "double", offerId },
      rewardedAdPreviewLabel: getRewardDoubleAdLabel(offer),
      ...(shouldUseRewardedAdPreview()
        ? { showRewardedAdPreview: true }
        : {}),
    });

    if (shouldUseRewardedAdPreview()) {
      return;
    }

    await this.completeWatchAdForRewardDouble();
  };

  completeWatchAdForRewardDouble = async () => {
    const pending = this.state.pendingRewardedAd;
    const offerId = pending?.type === "double" ? pending.offerId : null;
    const offer = (this.state.earnedRewardOffers || []).find(
      (entry) => entry.id === offerId
    );

    if (!offer) {
      this.setState({
        earnedRewardDoubleBusy: "",
        earnedRewardDoubleError: "",
        showRewardedAdPreview: false,
        pendingRewardedAd: null,
      });
      return;
    }

    try {
      await watchRewardedAdForDouble(Date.now(), {
        adsRemoved: this.isAdsRemoved(),
        skipDelay: shouldUseRewardedAdPreview(),
      });

      const patch = applyEarnedRewardDouble(offer, this.state);
      if (!patch) {
        throw new Error("Could not apply reward double.");
      }

      this.setState(
        (prev) => ({
          ...patch,
          doubledEarnedRewardIds: [
            ...(prev.doubledEarnedRewardIds || []),
            offer.id,
          ],
          earnedRewardDoubleBusy: "",
          earnedRewardDoubleError: "",
          showRewardedAdPreview: false,
          pendingRewardedAd: null,
        }),
        this.saveTreatState
      );
    } catch (error) {
      const message =
        error?.code === "ads-removed"
          ? "Ads are removed on this account."
          : error?.code === "ad-cap"
          ? "Daily ad limit reached. Your base reward is already saved."
          : "Could not play the ad. Try again in a moment.";
      this.setState({
        earnedRewardDoubleBusy: "",
        earnedRewardDoubleError: message,
        showRewardedAdPreview: false,
        pendingRewardedAd: null,
      });
    }
  };

  completeWatchAdForHeart = async () => {
    try {
      const result = await watchRewardedAdForHeart(Date.now(), {
        adsRemoved: this.isAdsRemoved(),
        skipDelay: shouldUseRewardedAdPreview(),
      });
      const heartsGranted = Math.max(1, Number(result?.hearts) || 1);

      this.setState(
        (prev) => {
          const nextHearts = grantHearts(prev.hearts, heartsGranted);
          const wasEmpty = prev.hearts <= 0;
          return {
            hearts: nextHearts,
            heartLastRefillAt:
              wasEmpty || prev.hearts >= MAX_HEARTS
                ? Date.now()
                : prev.heartLastRefillAt,
            heartRewardBusy: "",
            heartRewardError: "",
            showRewardedAdPreview: false,
            pendingRewardedAd: null,
          };
        },
        () => {
          this.saveHeartState();
          this.resumeRunAfterHeartGrant();
        }
      );
    } catch (error) {
      const message =
        error?.code === "ads-removed"
          ? "Ads are removed on this account."
          : error?.code === "ad-cap"
          ? "Daily ad limit reached. Wait for a refill or restore hearts."
          : "Could not play the ad. Try again in a moment.";
      this.setState({
        heartRewardBusy: "",
        heartRewardError: message,
        showRewardedAdPreview: false,
        pendingRewardedAd: null,
      });
    }
  };

  handleRewardedAdPreviewComplete = () => {
    if (this.state.pendingRewardedAd?.type === "double") {
      this.completeWatchAdForRewardDouble();
      return;
    }

    this.completeWatchAdForHeart();
  };

  handleRewardedAdPreviewCancel = () => {
    this.setState({
      showRewardedAdPreview: false,
      pendingRewardedAd: null,
      heartRewardBusy: "",
      heartRewardError: "",
      earnedRewardDoubleBusy: "",
      earnedRewardDoubleError: "",
    });
  };

  applyHeartPurchaseGrant = (grant = {}) => {
    if (grant.refillToMax) {
      return {
        hearts: refillHeartsToMax(this.state.hearts),
        heartLastRefillAt: Date.now(),
      };
    }

    const amount = Math.max(0, Math.floor(Number(grant.hearts) || 0));
    if (amount <= 0) {
      return null;
    }

    return {
      hearts: grantHearts(this.state.hearts, amount),
      heartLastRefillAt:
        this.state.hearts <= 0 || this.state.hearts >= MAX_HEARTS
          ? Date.now()
          : this.state.heartLastRefillAt,
    };
  };

  applyStorePurchaseResult = (result = {}) => {
    const grant = result.grant || {};
    const nextState = {};

    if (grant.removeAds) {
      const nextEntitlements = grantNoAdsEntitlement(this.state.iapEntitlements);
      nextState.iapEntitlements = this.persistIapEntitlements(nextEntitlements);
    }

    const nextHearts = this.applyHeartPurchaseGrant(grant);
    if (nextHearts) {
      Object.assign(nextState, nextHearts);
    }

    const treatAmount = Math.max(0, Math.floor(Number(grant.treats) || 0));
    if (treatAmount > 0) {
      nextState.treats = grantTreats(this.state.treats, treatAmount);
    }

    return nextState;
  };

  handlePurchaseHeartRefill = async () => {
    return this.handlePurchaseHeartsToContinue(getHeartRefillProductId());
  };

  handlePurchaseHeartsToContinue = async (productId) => {
    if (this.state.heartRewardBusy) {
      return;
    }

    const product = getIapProductById(productId);
    if (!product) {
      this.setState({
        heartRewardError: "That heart pack is unavailable.",
      });
      return;
    }

    const busyKey =
      productId === getHeartSingleProductId()
        ? "iap1"
        : productId === getHeartRefillProductId()
          ? "iap3"
          : productId === getHeartTenProductId()
            ? "iap10"
            : "iap";

    this.setState({ heartRewardBusy: busyKey, heartRewardError: "" });

    try {
      const result = await purchaseStoreProduct(productId);
      const nextGrant = this.applyStorePurchaseResult(result);

      this.setState(
        {
          ...nextGrant,
          heartRewardBusy: "",
          heartRewardError: "",
        },
        () => {
          this.saveHeartState();
          const heartsGranted = Math.max(
            0,
            Math.floor(Number(result?.grant?.hearts) || 0)
          );
          const notice = result?.grant?.refillToMax
            ? `Hearts refilled to ${MAX_HEARTS}.`
            : heartsGranted > 0
              ? `+${heartsGranted} heart${heartsGranted === 1 ? "" : "s"} added.`
              : "Purchase complete.";
          this.showPurchaseNotice("Hearts added", notice);
          this.resumeRunAfterHeartGrant();
        }
      );
    } catch (error) {
      if (error?.code === "iap-cancelled") {
        this.setState({
          heartRewardBusy: "",
          heartRewardError: "",
        });
        return;
      }
      this.setState({
        heartRewardBusy: "",
        heartRewardError:
          "Could not complete the purchase. Try again in a moment.",
      });
    }
  };

  handlePurchaseStoreProduct = async (productId) => {
    if (this.state.storeBusyProductId) {
      return;
    }

    const product = getIapProductById(productId);
    if (!product) {
      this.setState({ storeError: "That product is unavailable." });
      return;
    }

    if (product.id === getNoAdsProductId() && this.isAdsRemoved()) {
      this.setState({ storeError: "You already own No Ads." });
      return;
    }

    this.setState({ storeBusyProductId: productId, storeError: "" });

    try {
      const result = await purchaseStoreProduct(productId);
      const grant = result.grant || {};
      const nextGrant = this.applyStorePurchaseResult(result);
      const packCount = Math.max(0, Math.floor(Number(grant.packs) || 0));
      const boostBundleCount = Math.max(
        0,
        Math.floor(Number(grant.boostBundle) || 0)
      );
      const inventoryAfterBoosts =
        boostBundleCount > 0
          ? grantBoostBundle(this.state.inventory, boostBundleCount)
          : null;

      this.setState(
        {
          ...nextGrant,
          ...(inventoryAfterBoosts
            ? { inventory: inventoryAfterBoosts }
            : {}),
          storeBusyProductId: "",
          storeError: "",
        },
        () => {
          this.saveHeartState();
          this.saveTreatState();
          if (inventoryAfterBoosts) {
            this.saveInventoryState();
          }
          if (packCount > 0) {
            this.offerPacks(packCount, {
              sourceLabel: "Purchase complete.",
            });
            return;
          }

          if (boostBundleCount > 0) {
            this.showPurchaseNotice(
              "Boost Bundle added",
              `${formatBoostBundleLabel(boostBundleCount)} added to Inventory.`
            );
            return;
          }

          const heartsGranted = Math.max(
            0,
            Math.floor(Number(grant.hearts) || 0)
          );
          if (grant.removeAds) {
            this.showPurchaseNotice(
              "Purchase complete",
              "No Ads is now active on this account."
            );
            return;
          }
          if (grant.refillToMax) {
            this.showPurchaseNotice(
              "Hearts added",
              `Hearts refilled to ${MAX_HEARTS}.`
            );
            return;
          }
          if (heartsGranted > 0) {
            this.showPurchaseNotice(
              "Hearts added",
              `+${heartsGranted} heart${heartsGranted === 1 ? "" : "s"} added.`
            );
          }
        }
      );
    } catch (error) {
      if (error?.code === "iap-cancelled") {
        this.setState({
          storeBusyProductId: "",
          storeError: "",
        });
        return;
      }
      this.setState({
        storeBusyProductId: "",
        storeError: "Could not complete the purchase. Try again in a moment.",
      });
    }
  };

  handleRestoreStorePurchases = (result = {}) => {
    const restored = Array.isArray(result.restored) ? result.restored : [];
    const restoredNoAds = restored.some(
      (item) =>
        item?.productId === getNoAdsProductId() || item?.grant?.removeAds
    );

    if (restoredNoAds && !this.isAdsRemoved()) {
      this.setState({
        iapEntitlements: this.persistIapEntitlements(
          grantNoAdsEntitlement(this.state.iapEntitlements)
        ),
      });
    }
  };

  handleRedeemStoreCode = async (codeInput) => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      this.setState({
        storeError: "Sign in to redeem codes.",
        storeRedeemBusy: false,
        storeRedeemMessage: "",
      });
      return false;
    }

    if (this.state.storeRedeemBusy) {
      return false;
    }

    this.setState({
      storeRedeemBusy: true,
      storeRedeemMessage: "",
      storeError: "",
    });

    try {
      const result = await redeemCode(codeInput);
      const applied = applyRedemptionGrant(
        {
          treats: this.state.treats,
          hearts: this.state.hearts,
          heartLastRefillAt: this.state.heartLastRefillAt,
          cosmetics: this.state.cosmetics,
          iapEntitlements: this.state.iapEntitlements,
          storyProgress: this.state.storyProgress,
          collectionUnlocked: this.state.collectionUnlocked,
          profileImageId: this.state.profileImageId,
        },
        result?.rewards || {}
      );
      const nextRedemptions = markRedemptionClaimed(
        this.state.redemptions,
        result?.codeId,
        result?.redeemedAtMs || Date.now()
      );

      let nextState = { ...applied.nextState };
      if (applied.nextState.storyProgress) {
        const normalizedProgress = saveStoryProgress(
          applied.nextState.storyProgress
        );
        if (applied.resetStoryLevels) {
          clearBaseGameCompleted();
          setMaxUnlockedLevel(1);
        } else if (applied.unlockAllStoryLevels) {
          markBaseGameCompleted();
          setMaxUnlockedLevel(this.levels.length);
        }
        const collectionForCosmetics =
          applied.nextState.collectionUnlocked || this.state.collectionUnlocked;
        const nextCosmetics = this.syncCosmeticsWithStoryProgress(
          applied.nextState.cosmetics || this.state.cosmetics,
          normalizedProgress,
          collectionForCosmetics
        );
        nextState = {
          ...nextState,
          storyProgress: normalizedProgress,
          cosmetics: nextCosmetics,
          adminUnlockAll: applied.unlockAllStoryLevels
            ? true
            : applied.resetStoryLevels
              ? false
              : this.state.adminUnlockAll,
        };
      }

      const successMessage = [
        `Redeemed ${result?.title || "code"}.`,
        ...applied.rewardMessages,
      ].join(" ");

      this.setState(
        {
          ...nextState,
          redemptions: nextRedemptions,
          storeRedeemBusy: false,
          storeRedeemMessage: "",
          storeError: "",
        },
        () => {
          this.saveHeartState();
          this.saveTreatState();
          if (nextState.storyProgress && this.state.isAuthenticated && this.state.user?.uid) {
            setUserStoryProgress(
              this.state.user.uid,
              nextState.storyProgress
            ).catch((e) =>
              console.error("Failed to save story progress:", e)
            );
            if (nextState.cosmetics) {
              saveCosmeticsState(nextState.cosmetics);
              setUserCosmetics(this.state.user.uid, nextState.cosmetics).catch(
                (e) => console.error("Failed to save cosmetics:", e)
              );
            }
          } else if (nextState.cosmetics) {
            saveCosmeticsState(nextState.cosmetics);
          }
          if (applied.packCount > 0) {
            this.offerPacks(applied.packCount, {
              sourceLabel: "Code redeemed.",
              quiet: true,
            });
          }
          this.showPurchaseNotice("Code redeemed", successMessage);
        }
      );
      return true;
    } catch (error) {
      this.setState({
        storeRedeemBusy: false,
        storeRedeemMessage: "",
        storeError:
          String(error?.message || "").trim() ||
          "Could not redeem that code right now.",
      });
      return false;
    }
  };

  showPurchaseNotice = (title, message) => {
    const nextTitle = String(title || "").trim() || "Done";
    const nextMessage = String(message || "").trim();
    if (!nextMessage) {
      return;
    }
    this.setState({
      purchaseNotice: {
        title: nextTitle,
        message: nextMessage,
      },
    });
  };

  dismissPurchaseNotice = () => {
    this.setState({ purchaseNotice: null });
  };

  goStore = (tab = "featured") => {
    const progress = this.state.storyProgress || loadStoryProgress();
    if (!isStoreUnlocked(progress)) {
      return;
    }
    const allowed = new Set([
      "featured",
      "packs",
      "boosts",
      "hearts",
      "treats",
      "redeem",
      "extras",
    ]);
    let nextTab = allowed.has(tab) ? tab : "featured";
    if (nextTab === "extras") {
      nextTab = "redeem";
    }
    this.setState({ storeInitialTab: nextTab }, () => {
      this.requestLeaveRun("store");
    });
  };

  goStoreTreats = () => {
    this.goStore("treats");
  };

  handleClosePackReveal = () => {
    this.setState({ storePackResult: null });
  };

  handleOpenNextInventoryPack = () => {
    this.setState({ storePackResult: null }, () => {
      this.openInventoryCorgiPacks(1);
    });
  };

  offerPacks = (packCount = 1, { sourceLabel = "", quiet = false } = {}) => {
    const count = clampCorgiPackGrantCount(packCount);
    if (count <= 0) {
      return;
    }

    const nextInventory = grantCorgiPacks(this.state.inventory, count);
    this.setState(
      {
        packOffer: quiet
          ? null
          : {
              count,
              sourceLabel: String(sourceLabel || ""),
            },
        inventory: nextInventory,
      },
      this.saveInventoryState
    );
  };

  handlePackOfferDismiss = () => {
    this.setState({ packOffer: null });
  };

  openInventoryCorgiPacks = (packCount = 1) => {
    const requested = Math.max(1, Math.floor(Number(packCount) || 1));
    const spent = spendCorgiPacks(this.state.inventory, requested);
    if (!spent.ok) {
      return;
    }

    this.setState({ inventory: spent.inventory }, () => {
      this.saveInventoryState();
      this.openGrantedCorgiPacks(spent.spent);
    });
  };

  renderPackOverlays = () => {
    const remainingInventoryPacks = getCorgiPackCount(this.state.inventory);

    return (
      <>
        {this.state.packOffer ? (
          <PackOpenPrompt
            packCount={this.state.packOffer.count}
            sourceLabel={this.state.packOffer.sourceLabel}
            onConfirm={this.handlePackOfferDismiss}
          />
        ) : null}
        {this.state.purchaseNotice ? (
          <NoticePrompt
            title={this.state.purchaseNotice.title}
            message={this.state.purchaseNotice.message}
            onConfirm={this.dismissPurchaseNotice}
          />
        ) : null}
        {this.state.storePackResult && this.state.screen !== "store" ? (
          <StorePackRevealModal
            key={this.state.storePackResult.openedAt || "pack-overlay"}
            packResult={this.state.storePackResult}
            remainingInventoryPacks={remainingInventoryPacks}
            onClose={this.handleClosePackReveal}
            onOpenNextPack={this.handleOpenNextInventoryPack}
            onBuyMore={() => {
              this.handleClosePackReveal();
              this.goStore("packs");
            }}
          />
        ) : null}
      </>
    );
  };

  openGrantedCorgiPacks = (packCount = 1, treatsAfterSpend = null) => {
    const count = clampCorgiPackGrantCount(packCount);
    if (count <= 0) {
      return;
    }
    const startingTreats =
      treatsAfterSpend == null ? this.state.treats : treatsAfterSpend;
    const pack = openCorgiPacks({
      count,
      ownedIds: this.state.collectionUnlocked,
      ownedFoilIds: this.state.collectionFoilUnlocked,
    });
    const applied = applyPackDropsToCollection(
      pack.drops,
      this.state.collectionUnlocked,
      this.state.collectionFoilUnlocked
    );
    const nextTreats = grantTreats(startingTreats, pack.refund);
    const nextCosmetics = syncCollectionCosmetics(
      this.state.cosmetics,
      applied.collectionUnlocked,
      { preferredCardId: this.state.profileImageId }
    );

    this.setState(
      {
        treats: nextTreats,
        collectionUnlocked: applied.collectionUnlocked,
        collectionFoilUnlocked: applied.collectionFoilUnlocked,
        cosmetics: nextCosmetics,
        storeError: "",
        storePackResult: {
          ...pack,
          openedAt: Date.now(),
        },
      },
      () => {
        this.saveTreatState();
        if (this.state.cosmetics) {
          saveCosmeticsState(this.state.cosmetics);
        }
      }
    );
  };

  handleSpendTreatsShop = (itemId, options = {}) => {
    if (this.state.storePackBusy || this.state.storeBusyProductId) {
      return;
    }

    const item = getTreatsShopItemById(itemId);
    if (!item) {
      this.setState({ storeError: "That Treats item is unavailable." });
      return;
    }

    const isPack = item.packs > 0 || item.kind === "packs";
    if (isPack) {
      const packCount = Math.max(
        1,
        clampCorgiPackGrantCount(
          options.packCount != null ? options.packCount : item.packs || 1
        )
      );
      const cost = CORGI_PACK_COST * packCount;
      const spent = spendTreats(this.state.treats, cost);
      if (!spent.ok) {
        this.showPurchaseNotice(
          "Not enough Treats",
          `You need ${formatTreats(cost)} for ${packCount} Corgi Pack${
            packCount === 1 ? "" : "s"
          }. You have ${formatTreats(this.state.treats)}.`
        );
        return;
      }

      this.setState(
        {
          treats: spent.treats,
          storeError: "",
          storePackResult: null,
        },
        () => {
          this.saveTreatState();
          this.offerPacks(packCount, {
            sourceLabel: "Treats spent.",
          });
        }
      );
      return;
    }

    const isBoost =
      item.kind === "boosts" ||
      item.boostBundle > 0 ||
      Boolean(item.boostItemId);
    if (isBoost) {
      const spent = spendTreats(this.state.treats, item.cost);
      if (!spent.ok) {
        this.showPurchaseNotice(
          "Not enough Treats",
          `Need ${formatTreats(item.cost)} for ${item.title}. You have ${formatTreats(
            this.state.treats
          )}.`
        );
        return;
      }

      let nextInventory = this.state.inventory;
      let noticeBody = "";

      if (item.boostBundle > 0) {
        nextInventory = grantBoostBundle(nextInventory, item.boostBundle);
        noticeBody = `Spent ${formatTreats(item.cost)} for ${formatBoostBundleLabel(
          item.boostBundle
        )}.`;
      } else if (item.boostItemId) {
        nextInventory = grantInventoryItem(
          nextInventory,
          item.boostItemId,
          item.boostAmount || 1
        );
        noticeBody = `Spent ${formatTreats(item.cost)} for ${item.title}.`;
      }

      this.setState(
        {
          treats: spent.treats,
          inventory: nextInventory,
          storeError: "",
        },
        () => {
          this.saveTreatState();
          this.saveInventoryState();
          this.showPurchaseNotice("Boost added", noticeBody);
        }
      );
      return;
    }

    const spent = spendTreats(this.state.treats, item.cost);
    if (!spent.ok) {
      this.showPurchaseNotice(
        "Not enough Treats",
        `Need ${formatTreats(item.cost)} for ${item.title}. You have ${formatTreats(
          this.state.treats
        )}.`
      );
      return;
    }

    const heartAmount = Math.max(0, Math.floor(Number(item.hearts) || 0));
    const nextHearts =
      heartAmount > 0 ? grantHearts(this.state.hearts, heartAmount) : null;

    this.setState(
      {
        treats: spent.treats,
        ...(nextHearts
          ? {
              hearts: nextHearts,
              heartLastRefillAt:
                this.state.hearts <= 0 || this.state.hearts >= MAX_HEARTS
                  ? Date.now()
                  : this.state.heartLastRefillAt,
            }
          : {}),
        storeError: "",
      },
      () => {
        this.saveTreatState();
        if (nextHearts) {
          this.saveHeartState();
          this.showPurchaseNotice(
            "Hearts added",
            `Spent ${formatTreats(item.cost)} for +${heartAmount} heart${
              heartAmount === 1 ? "" : "s"
            }.`
          );
        }
      }
    );
  };

  declineHeartEmpty = () => {
    this.setState(
      {
        showHeartEmptyOverlay: false,
        heartRewardBusy: "",
        heartRewardError: "",
      },
      this.endRunAsLoss
    );
  };

  endRunAsLoss = () => {
    this.stopAdminAutoComplete();
    this.stopRunTimer();
    this.clearAnxiousTimers();

    this.setState({
      ...this.getFinishedRunTimingState(),
      ...this.applyTreatGrant("loss"),
      screen: "recap",
      runOutcome: "loss",
      runEndReason: "wrong-card",
      runLevelReached: this.state.currentLevel + 1,
      storyUnlockMessage: "",
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      showHeartEmptyOverlay: false,
      heartRewardBusy: "",
      heartRewardError: "",
      pendingWrongCardId: null,
      pendingNextLevel: null,
      forgivingMistakeMessage: "",
    }, this.saveTreatState);
  };

  isGuidedStoryRun = (runConfig = null) => {
    const config = runConfig || this.getActiveRunConfig();
    return (
      isStoryRun(config) && Boolean(config?.tutorialBehavior?.guidedTutorial)
    );
  };

  shouldCoverGameBarForStoryPrompt = () => {
    const runConfig = this.getActiveRunConfig();
    if (!isStoryRun(runConfig)) {
      return false;
    }

    const levelId = Number(runConfig.storyLevelId);
    if (!Number.isFinite(levelId) || levelId > 3) {
      return false;
    }

    return Boolean(
      this.state.forgivingMistakeMessage ||
        this.state.showGuideHeartDemo ||
        this.state.showHeartContinueOverlay
    );
  };

  getUnusedStoryCardIds = (clicked = this.state.clicked, cards = this.state.cards) => {
    const used = new Set(
      (clicked || [])
        .filter((entry) => entry !== false)
        .map((entry) => String(normalizeId(entry)))
    );

    return (cards || [])
      .map((card) => String(normalizeId(card.id)))
      .filter((id) => !used.has(id));
  };

  getUsedStoryCardIdsOnBoard = (
    clicked = this.state.clicked,
    cards = this.state.cards
  ) => {
    const used = new Set(
      (clicked || [])
        .filter((entry) => entry !== false)
        .map((entry) => String(normalizeId(entry)))
    );
    const onBoard = new Set(
      (cards || []).map((card) => String(normalizeId(card.id)))
    );

    return Array.from(used).filter((id) => onBoard.has(id));
  };

  getGuidedCoachMessage = (step = this.state.storyGuideStep, pickCount = null) => {
    const picks =
      pickCount == null ? this.getStoryBoardPickCount() : pickCount;

    if (step === "pick-new") {
      if (picks === 0) {
        return "Tap the glowing card. Select every card once — no repeats.";
      }
      return "Correct! Cards shuffled. Tap the next glowing card you have not used yet.";
    }

    if (step === "pick-wrong") {
      return "Now tap a glowing card you already picked to see what a mistake does.";
    }

    if (step === "finish-board") {
      return "Great — keep tapping glowing new cards to finish clearing the board.";
    }

    return "";
  };

  syncGuidedHighlight = (
    step = this.state.storyGuideStep,
    clicked = this.state.clicked,
    cards = this.state.cards
  ) => {
    if (!this.isGuidedStoryRun() || !step) {
      return {
        storyGuideHighlightId: null,
      };
    }

    if (step === "pick-wrong") {
      const usedOnBoard = this.getUsedStoryCardIdsOnBoard(clicked, cards);
      return {
        storyGuideHighlightId: usedOnBoard[0] || null,
      };
    }

    if (step === "pick-new" || step === "finish-board") {
      const unused = this.getUnusedStoryCardIds(clicked, cards);
      return {
        storyGuideHighlightId: unused[0] || null,
      };
    }

    return {
      storyGuideHighlightId: null,
    };
  };

  beginGuidedStoryLesson = () => {
    if (!this.isGuidedStoryRun()) {
      return;
    }

    const step = "pick-new";
    const highlight = this.syncGuidedHighlight(
      step,
      this.state.clicked,
      this.state.cards
    );

    this.setState({
      storyGuideStep: step,
      storyGuideWrongLessonDone: false,
      showGuideHeartDemo: false,
      storyAlertKind: "practice",
      forgivingMistakeMessage: this.getGuidedCoachMessage(step, 0),
      ...highlight,
    });
  };

  dismissGuideHeartDemo = () => {
    const step = "finish-board";
    const highlight = this.syncGuidedHighlight(
      step,
      this.state.clicked,
      this.state.cards
    );

    this.setState({
      showGuideHeartDemo: false,
      storyGuideStep: step,
      storyGuideWrongLessonDone: true,
      storyAlertKind: "practice",
      forgivingMistakeMessage: this.getGuidedCoachMessage(step),
      ...highlight,
    });
  };

  handleWrongCard = (id) => {
    const runConfig = this.getActiveRunConfig();
    const normalizedId = String(normalizeId(id));

    if (this.isGuidedStoryRun(runConfig)) {
      if (this.state.storyGuideStep === "pick-wrong") {
        if (normalizedId !== String(this.state.storyGuideHighlightId || "")) {
          this.setState({
            storyAlertKind: "practice",
            forgivingMistakeMessage:
              "Tap the glowing card you already picked to continue the lesson.",
          });
          return;
        }

        this.setState({
          showGuideHeartDemo: true,
          storyAlertKind: "perfect-reset",
          forgivingMistakeMessage:
            "Mistake demo: this is when hearts matter. No real heart is spent here.",
          storyGuideHighlightId: null,
        });
        return;
      }

      this.setState({
        storyAlertKind: "practice",
        forgivingMistakeMessage:
          this.state.storyGuideStep === "finish-board" ||
          this.state.storyGuideStep === "pick-new"
            ? "That card was already selected. Tap the glowing new card instead."
            : "Follow the glowing card to continue the lesson.",
      });
      return;
    }

    const forgivingMistakes = Boolean(
      runConfig?.tutorialBehavior?.forgivingMistakes
    );

    if (forgivingMistakes && isStoryRun(runConfig)) {
      this.setState({
        storyAlertKind: "practice",
        forgivingMistakeMessage:
          "That card was already selected. Try a different card.",
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
      });
      return;
    }

    if (this.state.runBoostSecondChanceArmed) {
      const spent = spendInventoryItem(
        this.state.inventory,
        BOOST_ITEM_IDS.SECOND_CHANCE,
        1
      );
      const nextInventory = spent.ok
        ? spent.inventory
        : this.state.inventory;

      this.setState(
        {
          inventory: nextInventory,
          runBoostUsed: {
            ...this.state.runBoostUsed,
            second_chance: true,
          },
          runBoostSecondChanceArmed: false,
          pendingWrongCardId: null,
          showHeartContinueOverlay: false,
          showHeartEmptyOverlay: false,
          showLevelCompleteOverlay: false,
          pendingNextLevel: null,
          forgivingMistakeMessage: "",
          boostMessage: "Shield — mistake forgiven!",
        },
        () => {
          if (spent.ok) {
            this.saveInventoryState();
          }
          this.clearBoostMessageLater();
        }
      );
      return;
    }

    // Perfect-clear Story levels: void the current clear.
    // House-heart teaching (L3): show the continue prompt first — free continue.
    if (
      isStoryRun(runConfig) &&
      runConfig.requirePerfectClears &&
      !this.state.hardcoreMode
    ) {
      const houseHeart = Boolean(
        runConfig?.tutorialBehavior?.houseHeartContinue
      );

      if (houseHeart) {
        this.setState({
          currentPerfectStreak: 0,
          showHeartContinueOverlay: true,
          showHeartEmptyOverlay: false,
          pendingWrongCardId: id,
          showLevelCompleteOverlay: false,
          pendingNextLevel: null,
          forgivingMistakeMessage: "",
        });
        return;
      }

      this.resetStoryBoardForNextClear({
        message:
          "Perfect clear reset — new cards dealt. Select each once without repeats.",
        alertKind: "perfect-reset",
      });
      return;
    }

    const nextHeartState = this.getRefilledHeartState(
      this.state.hearts,
      this.state.heartLastRefillAt
    );

    // Hardcore ignores hearts — wrong card ends the run.
    if (this.state.hardcoreMode) {
      this.setState(
        {
          ...nextHeartState,
          currentPerfectStreak: 0,
        },
        () => {
          this.saveHeartState();
          this.endRunAsLoss();
        }
      );
      return;
    }

    // Out of hearts: monetization funnel (Wait / Ad / Buy) instead of a dead end.
    if (nextHeartState.hearts <= 0) {
      this.setState(
        {
          ...nextHeartState,
          currentPerfectStreak: 0,
          showHeartEmptyOverlay: true,
          showHeartContinueOverlay: false,
          heartRewardBusy: "",
          heartRewardError: "",
          pendingWrongCardId: id,
          showLevelCompleteOverlay: false,
          pendingNextLevel: null,
          forgivingMistakeMessage: "",
        },
        this.saveHeartState
      );
      return;
    }

    this.setState(
      {
        ...nextHeartState,
        currentPerfectStreak: 0,
        showHeartContinueOverlay: true,
        showHeartEmptyOverlay: false,
        pendingWrongCardId: id,
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
        forgivingMistakeMessage: "",
      },
      this.saveHeartState
    );
  };

  spendHeartToContinue = () => {
    const runConfig = this.getActiveRunConfig();
    const houseHeart = this.isHouseHeartContinueActive();

    if (houseHeart) {
      this.setState(
        {
          showHeartContinueOverlay: false,
          showHeartEmptyOverlay: false,
          heartRewardBusy: "",
          heartRewardError: "",
          pendingWrongCardId: null,
        },
        () => {
          if (runConfig.requirePerfectClears && !this.state.hardcoreMode) {
            this.resetStoryBoardForNextClear({
              message:
                "On the house — clear reset. New cards dealt. Select each once without repeats.",
              alertKind: "perfect-reset",
            });
          }
        }
      );
      return;
    }

    this.setState((prevState) => {
      const nextHeartState = this.getRefilledHeartState(
        prevState.hearts,
        prevState.heartLastRefillAt
      );

      if (nextHeartState.hearts <= 0) {
        return {
          ...nextHeartState,
          showHeartContinueOverlay: false,
          showHeartEmptyOverlay: true,
          heartRewardBusy: "",
          heartRewardError: "",
          // Keep pendingWrongCardId so a grant can resume this run.
        };
      }

      const spentFromFull = nextHeartState.hearts >= MAX_HEARTS;

      return {
        hearts: nextHeartState.hearts - 1,
        heartLastRefillAt: spentFromFull
          ? Date.now()
          : nextHeartState.heartLastRefillAt,
        showHeartContinueOverlay: false,
        showHeartEmptyOverlay: false,
        heartRewardBusy: "",
        heartRewardError: "",
        pendingWrongCardId: null,
        runHeartsSpent: (prevState.runHeartsSpent || 0) + 1,
      };
    }, this.saveHeartState);
  };

  declineHeartContinue = () => {
    this.endRunAsLoss();
  };

  isHouseHeartContinueActive = () => {
    const runConfig = this.getActiveRunConfig();
    return (
      isStoryRun(runConfig) &&
      Boolean(runConfig?.tutorialBehavior?.houseHeartContinue)
    );
  };

  dismissStoryIntro = () => {
    if (!this.state.showStoryIntro) {
      return;
    }

    this.setState(
      {
        showStoryIntro: false,
        runStartedAt: Date.now(),
        runElapsedMs: 0,
      },
      () => {
        this.startRunTimer();
        this.beginGuidedStoryLesson();
        this.scheduleAnxiousScramble();
      }
    );
  };

  cancelStoryIntro = () => {
    if (!this.state.showStoryIntro) {
      return;
    }

    this.resetGameToStart();
    this.navigateBackFromHistory();
  };

  handleClick = (event) => {
    const id = event.target.id;
    const isFoil = event.currentTarget.dataset.foil === "true";
    const normalizedId = String(normalizeId(id));
    const matchedCard = (this.state.cards || []).find(
      (entry) => String(normalizeId(entry.id)) === normalizedId
    );
    const hiddenLootKind = matchedCard?.hiddenLootKind || null;

    if (
      this.state.showStoryIntro ||
      this.state.showGuideHeartDemo ||
      this.state.showShinyDiscovery ||
      this.state.hiddenLootDiscoveryKind ||
      this.state.showHeartContinueOverlay ||
      this.state.showHeartEmptyOverlay ||
      this.state.showResumeOverlay
    ) {
      return;
    }

    if (this.state.runMode === "blitz" && this.getBlitzRemainingMs() <= 0) {
      this.endRunFromBlitzTimeout();
      return;
    }

    const runConfig = this.getActiveRunConfig();
    const guided = this.isGuidedStoryRun(runConfig);

    if (guided && this.state.storyGuideStep === "pick-wrong") {
      if (this.findId(id)) {
        this.handleWrongCard(id);
        return;
      }

      this.setState({
        storyAlertKind: "practice",
        forgivingMistakeMessage:
          "For this step, tap a glowing card you already picked.",
      });
      return;
    }

    if (
      guided &&
      (this.state.storyGuideStep === "pick-new" ||
        this.state.storyGuideStep === "finish-board")
    ) {
      const highlightId = String(this.state.storyGuideHighlightId || "");
      if (!highlightId || normalizedId !== highlightId) {
        this.setState({
          storyAlertKind: "practice",
          forgivingMistakeMessage:
            "Tap the glowing card to continue the lesson.",
        });
        return;
      }
    }

    if (this.findId(id)) {
      this.handleWrongCard(id);
      return;
    }

    const clickIndex = this.returnFirstNull();
    const nextClicked = this.state.clicked.map((elem, i) =>
      i === clickIndex ? id : elem
    );
    const boardCleared = nextClicked.every((entry) => entry !== false);
    const isEndless = isEndlessRun(runConfig);
    const isStory = isStoryRun(runConfig);

    // Story has no score. Endless: clicks advance levels; weighted points are separate.
    const pointsPerClick = isEndless
      ? getEndlessPointsPerClick(
          runConfig.boardSize,
          buildEndlessScoreModifiersFromRunConfig(runConfig)
        )
      : 0;
    const nextProgressClicks = isEndless
      ? (this.state.endlessProgressClicks || 0) + 1
      : this.state.endlessProgressClicks || 0;
    const newScore = isEndless
      ? (this.state.score || 0) + pointsPerClick
      : 0;
    const canTrackHighScore = isEndless && this.isHighScoreEnabled();
    const newTopScore =
      canTrackHighScore && newScore > this.state.topScore
        ? newScore
        : this.state.topScore;
    const nextPickCount = nextClicked.filter((entry) => entry !== false).length;

    let nextGuideStep = this.state.storyGuideStep;
    if (guided) {
      if (
        this.state.storyGuideStep === "pick-new" &&
        nextPickCount >= 2 &&
        !this.state.storyGuideWrongLessonDone
      ) {
        nextGuideStep = "pick-wrong";
      } else if (this.state.storyGuideStep === "pick-new") {
        nextGuideStep = "pick-new";
      } else if (this.state.storyGuideStep === "finish-board") {
        nextGuideStep = "finish-board";
      }
    }

    const nextPerfectStreak = isEndless
      ? (this.state.currentPerfectStreak || 0) + 1
      : 0;

    if (isEndless) {
      this.recordEndlessPerfectStreak(
        nextPerfectStreak,
        runConfig.boardSize
      );
      this.recordEndlessRunScore(runConfig.boardSize, newScore);
    }

    const shouldFreezeBoard = this.state.runBoostFreezeArmed;
    const freezeSpend = shouldFreezeBoard
      ? spendInventoryItem(this.state.inventory, BOOST_ITEM_IDS.FREEZE, 1)
      : null;
    const freezeInventory =
      freezeSpend && freezeSpend.ok
        ? freezeSpend.inventory
        : this.state.inventory;
    let hiddenLootGranted = false;

    this.setState((prev) => {
      let nextInventory = shouldFreezeBoard ? freezeInventory : prev.inventory;

      if (isEndless && hiddenLootKind) {
        if (isHiddenPackKind(hiddenLootKind)) {
          nextInventory = grantCorgiPacks(nextInventory, 1);
          hiddenLootGranted = true;
        } else if (isHiddenBoostKind(hiddenLootKind)) {
          nextInventory = grantInventoryItem(nextInventory, hiddenLootKind, 1);
          hiddenLootGranted = true;
        }
      }

      const patch = {
        inventory: nextInventory,
        runCorrectClicks: (prev.runCorrectClicks || 0) + 1,
        runFoilsFound: (prev.runFoilsFound || 0) + (isFoil ? 1 : 0),
        score: newScore,
        endlessProgressClicks: nextProgressClicks,
        topScore: newTopScore,
        clicked: nextClicked,
        currentPerfectStreak: nextPerfectStreak,
        storyAlertKind: guided ? "practice" : "",
        forgivingMistakeMessage: "",
        storyGuideStep: guided ? nextGuideStep : prev.storyGuideStep,
        runBoostRevealHighlightId:
          prev.runBoostRevealHighlightId &&
          String(normalizeId(prev.runBoostRevealHighlightId)) === normalizedId
            ? null
            : prev.runBoostRevealHighlightId,
        runBoostFreezeArmed: shouldFreezeBoard
          ? false
          : prev.runBoostFreezeArmed,
        runBoostUsed: shouldFreezeBoard
          ? { ...prev.runBoostUsed, freeze: true }
          : prev.runBoostUsed,
        boostMessage: shouldFreezeBoard
          ? "Freeze held the board!"
          : prev.boostMessage,
      };

      if (isEndless && hiddenLootKind) {
        if (isHiddenPackKind(hiddenLootKind)) {
          patch.runHiddenPacksFound = (prev.runHiddenPacksFound || 0) + 1;
          if (!hasSeenHiddenPackDiscovery()) {
            patch.hiddenLootDiscoveryKind = "pack";
          }
        } else if (isHiddenBoostKind(hiddenLootKind)) {
          patch.runHiddenBoostsFound = (prev.runHiddenBoostsFound || 0) + 1;
          if (!hasSeenHiddenBoostDiscovery()) {
            patch.hiddenLootDiscoveryKind = "boost";
          }
        }
      }

      return patch;
    }, () => {
      if (shouldFreezeBoard) {
        if (freezeSpend && freezeSpend.ok) {
          this.saveInventoryState();
        }
        this.clearBoostMessageLater();
      } else if (hiddenLootGranted) {
        this.saveInventoryState();
      }
    });

    const shuffledCards = shouldFreezeBoard ? this.state.cards : this.shuffle();

    if (isStory) {
      if (!boardCleared) {
        if (guided) {
          const highlight = this.syncGuidedHighlight(
            nextGuideStep,
            nextClicked,
            shuffledCards
          );
          this.setState({
            storyGuideStep: nextGuideStep,
            storyAlertKind:
              nextGuideStep === "pick-wrong" ? "perfect-reset" : "practice",
            forgivingMistakeMessage: this.getGuidedCoachMessage(
              nextGuideStep,
              nextPickCount
            ),
            ...highlight,
          });
        }
        return;
      }

      const clearTarget = getStoryClearTarget(runConfig) || 1;
      const nextClears = (this.state.storyClearsCompleted || 0) + 1;
      const boardSize = runConfig.boardSize || nextClicked.length;
      const isPerfect = Boolean(runConfig.requirePerfectClears);

      if (nextClears >= clearTarget) {
        this.setState(
          {
            storyClearsCompleted: nextClears,
            clicked: Array(boardSize).fill(false),
            storyAlertKind: guided ? "clear-complete" : "",
            forgivingMistakeMessage: guided
              ? "Board cleared! You’ve learned the core loop — next level is yours to try alone."
              : "",
            storyGuideStep: null,
            storyGuideHighlightId: null,
            showGuideHeartDemo: false,
          },
          () => {
            this.finishStoryLevel();
          }
        );
        return;
      }

      const label = isPerfect ? "Perfect stage cleared" : "Stage cleared";

      this.resetStoryBoardForNextClear({
        message: `${label} ${nextClears}/${clearTarget}! New cards dealt — keep going.`,
        alertKind: isPerfect ? "perfect-clear" : "clear-complete",
        storyClearsCompleted: nextClears,
      });
      return;
    }

    const currentLevelConfig = this.getLevelConfig(this.state.currentLevel);

    if (
      isEndless &&
      nextProgressClicks >= currentLevelConfig.threshold
    ) {
      const nextLevel = this.state.currentLevel + 1;
      const completedLevelNumber = this.state.currentLevel + 1;

      this.recordEndlessBoardLevel(
        runConfig.boardSize,
        completedLevelNumber,
        runConfig
      );

      this.setState({
        showLevelCompleteOverlay: true,
        pendingNextLevel: nextLevel,
        runLevelsCompleted: (this.state.runLevelsCompleted || 0) + 1,
      });
    }
  };

  finishStoryLevel = () => {
    this.stopAdminAutoComplete();

    const runConfig = this.getActiveRunConfig();
    const storyLevelId = runConfig?.storyLevelId;

    if (!isStoryRun(runConfig) || !storyLevelId) {
      return;
    }

    const nextProgress = completeStoryLevel(
      storyLevelId,
      this.state.storyProgress,
      { hardMode: isStoryHardModeActive(this.state.storyProgress) }
    );
    const storyLevel = getStoryLevelById(storyLevelId);
    const cosmeticGrant = grantCosmeticsForStoryLevel(
      this.state.cosmetics,
      storyLevel
    );
    const preferredFrameId =
      cosmeticGrant.grantedItems.find(
        (item) => item && item.type === COSMETIC_TYPES.AVATAR_FRAME
      )?.id || null;
    const preferredTitleId =
      cosmeticGrant.grantedItems.find(
        (item) => item && item.type === COSMETIC_TYPES.TITLE
      )?.id || null;
    let cosmeticsAfterGrant = cosmeticGrant.state;
    let autoEquipped = false;
    if (preferredFrameId) {
      const frameResult = autoEquipAvatarFrameIfEmpty(
        cosmeticsAfterGrant,
        preferredFrameId
      );
      cosmeticsAfterGrant = frameResult.state;
      autoEquipped = autoEquipped || frameResult.equipped;
    }
    if (preferredTitleId) {
      const titleResult = autoEquipTitleIfEmpty(
        cosmeticsAfterGrant,
        preferredTitleId
      );
      cosmeticsAfterGrant = titleResult.state;
      autoEquipped = autoEquipped || titleResult.equipped;
    }
    const treatGrant = this.applyTreatGrant("win", runConfig);
    const packsGranted = getStoryLevelPackReward(storyLevelId, true);
    const boostBundlesGranted = getStoryLevelBoostBundleReward(
      storyLevelId,
      true
    );
    const inventoryAfterBoosts =
      boostBundlesGranted > 0
        ? grantBoostBundle(this.state.inventory, boostBundlesGranted)
        : this.state.inventory;

    this.persistStoryProgress(nextProgress);
    if (cosmeticGrant.grantedItems.length > 0 || autoEquipped) {
      this.persistCosmetics(cosmeticsAfterGrant);
    }
    this.stopRunTimer();
    this.clearAnxiousTimers();

    if (
      Number(runConfig.boardSize) === 16 &&
      (this.state.runHeartsSpent || 0) >= MAX_HEARTS
    ) {
      this.markDontCallItAComeback();
    }

    this.setState(
      {
        ...this.getFinishedRunTimingState(),
        ...treatGrant,
        ...this.getRecapEarnedRewardState("win", treatGrant),
        inventory: inventoryAfterBoosts,
        runBoostBundlesEarned: boostBundlesGranted,
        screen: "recap",
        runOutcome: "win",
        runEndReason: "completed-story-level",
        runLevelReached: storyLevelId,
        runLevelsCompleted: (this.state.runLevelsCompleted || 0) + 1,
        storyUnlockMessage: getStoryUnlockToastMessages(storyLevelId).join(" · "),
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
        showStoryIntro: false,
        forgivingMistakeMessage: "",
        showReviewPrompt: shouldOfferReviewPrompt({
          storyWins: (nextProgress.completedLevelIds || []).length,
          reviewState: loadReviewPromptState(),
        }),
      },
      () => {
        this.saveTreatState();
        if (boostBundlesGranted > 0) {
          this.saveInventoryState();
        }
        if (packsGranted > 0) {
          this.offerPacks(packsGranted, {
            sourceLabel: "Story reward.",
          });
        }
      }
    );
  };

  finishRun = () => {
    markBaseGameCompleted();
    this.stopRunTimer();

    const treatGrant = this.applyTreatGrant("win");

    this.setState({
      ...this.getFinishedRunTimingState(),
      ...treatGrant,
      ...this.getRecapEarnedRewardState("win", treatGrant),
      screen: "recap",
      runOutcome: "win",
      runEndReason: "completed-main-run",
      runLevelReached: this.state.currentLevel + 1,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,
    }, this.saveTreatState);
  };

  startNextLevel = () => {
    const nextLevel = this.state.pendingNextLevel;
    if (nextLevel == null) return;

    const runConfig = this.getActiveRunConfig();

    this.setState({
      showLevelCompleteOverlay: false,
      showLevelOverlay: false,
    });

    if (nextLevel >= MAX_ENDLESS_LEVEL) {
      this.stopRunTimer();

      const treatGrant = this.applyTreatGrant("win");

      this.setState({
        ...this.getFinishedRunTimingState(),
        ...treatGrant,
        ...this.getRecapEarnedRewardState("win", treatGrant),
        screen: "recap",
        runOutcome: "win",
        runEndReason: "max-level",
        runLevelReached: MAX_ENDLESS_LEVEL,
        showLevelCompleteOverlay: false,
        showLevelOverlay: false,
        pendingNextLevel: null,
      }, this.saveTreatState);
      return;
    }

    if (this.isFinalMainLevelCompleteChoice()) {
      markBaseGameCompleted();
    }

    const nextLevelConfig = this.getLevelConfig(nextLevel);
    const fetchedNewCards = this.getNewCards(nextLevelConfig.cards);

    this.setState(
      this.buildBoardCardsStatePatch(
        this.prepareCardsForBoard(
          this.shuffleArray(fetchedNewCards).slice(0, nextLevelConfig.cards),
          this.state.twistedMode
        ),
        {
          showLevelCompleteOverlay: false,
          showLevelOverlay: false,
          pendingNextLevel: null,
          currentLevel: nextLevel,
          shyCorgisFlipLocked: false,
          shyHiddenCardIds: [],
          clicked: Array(nextLevelConfig.cards).fill(false),
          isBoardVisible: true,
        }
      ),
      () => {
        if (!isEndlessRun(runConfig) && !isStoryRun(runConfig)) {
          setMaxUnlockedLevel(Math.min(nextLevel + 1, this.levels.length));
        }
      }
    );
  };

  resetGameToStart = () => {
    this.stopRunTimer();
    this.clearAnxiousTimers();

    this.setState(
      this.buildBoardCardsStatePatch(
        this.prepareCardsForBoard(this.getNewCards(this.levels[0].cards), false),
        {
      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      showHeartEmptyOverlay: false,
      heartRewardBusy: "",
      heartRewardError: "",
      pendingNextLevel: null,
      pendingWrongCardId: null,

      twistedMode: false,
      shyCorgisMode: false,
      anxiousMode: false,
      anxiousWarning: false,
      hardcoreMode: false,
      shyCorgisFlipLocked: false,
      shyHiddenCardIds: [],
      score: 0,
      topScore: this.state.topScore,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],
      endlessProgressClicks: 0,

      runMode: "normal",
      runConfig: null,
      runStartedAt: null,
      runElapsedMs: 0,
      runFinalElapsedMs: 0,
      runEndReason: "",
      storyUnlockMessage: "",
      storyClearsCompleted: 0,
      showStoryIntro: false,
      storyBoardDealKey: 0,
      storyAlertKind: "",
      storyGuideStep: null,
      storyGuideHighlightId: null,
      storyGuideWrongLessonDone: false,
      showGuideHeartDemo: false,
      showShinyDiscovery: false,
      hiddenLootDiscoveryKind: null,
      forgivingMistakeMessage: "",
      showExitConfirm: false,
      pendingExitDestination: null,
      showResumeOverlay: false,
      runPausedAt: null,
      runPauseAccumulatedMs: 0,
        }
      )
    );
  };

  goHome = () => {
    this.requestLeaveRun("home");
  };

  goStoryMode = (options = {}) => {
    this.resetGameToStart();
    this.setState((prev) => {
      const fromRunEnd =
        prev.screen === "recap" || prev.screen === "game";
      return {
        screen: "story",
        screenHistory: fromRunEnd
          ? ["home"]
          : this.buildScreenHistory(
              prev.screen,
              "story",
              prev.screenHistory
            ),
        storyProgress: loadStoryProgress(),
        storyMapPage: options.mapPage || "surface",
      };
    });
  };

  continueNextStoryLevel = () => {
    const runConfig = this.getActiveRunConfig();
    const currentLevelId = Number(runConfig?.storyLevelId);

    if (!Number.isFinite(currentLevelId)) {
      this.goStoryMode();
      return;
    }

    // After Overworld finale, land on the Underworld map so players choose L13.
    if (currentLevelId === getStoryMainLevelCount()) {
      this.goStoryMode({ mapPage: "underworld" });
      return;
    }

    const nextConfig = buildStoryRunConfig(currentLevelId + 1);

    if (!nextConfig) {
      this.goStoryMode();
      return;
    }

    this.goPlay(nextConfig);
  };

  goEndlessMode = () => {
    this.resetGameToStart();
    this.setState((prev) => {
      const fromRunEnd =
        prev.screen === "recap" || prev.screen === "game";
      return {
        screen: "endless",
        screenHistory: fromRunEnd
          ? ["home"]
          : this.buildScreenHistory(
              prev.screen,
              "endless",
              prev.screenHistory
            ),
        storyProgress: loadStoryProgress(),
      };
    });
  };

  goHelp = () => {
    this.pushScreen("help");
  };

  goPrivacyPolicy = () => {
    if (this.state.screen === "legal") {
      this.setState({ legalDocId: "privacy" });
      return;
    }
    this.pushScreen("legal", { legalDocId: "privacy" });
  };

  goTermsOfService = () => {
    if (this.state.screen === "legal") {
      this.setState({ legalDocId: "terms" });
      return;
    }
    this.pushScreen("legal", { legalDocId: "terms" });
  };

  renderAppFooter = (props = {}) => (
    <AppFooter
      onPrivacyPolicy={this.goPrivacyPolicy}
      onTermsOfService={this.goTermsOfService}
      {...props}
    />
  );

  goCollection = (options = {}) => {
    this.pushScreen("collection", {
      collectionPickProfile: Boolean(options.pickProfile),
    });
  };

  goChooseProfileImage = () => {
    this.goCollection({ pickProfile: true });
  };

  selectProfileImageFromCollection = (cardId) => {
    const cosmeticId = getAvatarCosmeticIdForCard(cardId);
    if (cosmeticId) {
      this.handleEquipCosmetic(cosmeticId);
    } else {
      this.selectProfileImage(cardId);
    }

    if (this.state.collectionPickProfile) {
      this.goBack();
    }
  };

  goAchievements = () => {
    this.pushScreen("achievements");
  };

  goCosmetics = () => {
    this.pushScreen("cosmetics");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  goProfile = () => {
    this.requestLeaveRun("profile");
  };

  goFriends = () => {
    this.requestLeaveRun("friends");
  };

  goFriendProfile = (friendUid) => {
    if (!friendUid) {
      return;
    }

    this.setState(
      (prev) => ({
        screen: "friendProfile",
        screenHistory: this.buildScreenHistory(
          prev.screen,
          "friendProfile",
          prev.screenHistory
        ),
        viewingFriendUid: friendUid,
        viewingFriendProfile: null,
        viewingFriendLoading: true,
        viewingFriendError: "",
      }),
      () => this.loadViewingFriendProfile(friendUid)
    );
  };

  loadViewingFriendProfile = async (friendUid) => {
    const viewerUid = this.state.user?.uid;
    const targetUid = friendUid || this.state.viewingFriendUid;

    if (!this.state.isAuthenticated || !viewerUid || !targetUid) {
      this.setState({
        viewingFriendLoading: false,
        viewingFriendError: "Sign in to view player profiles.",
        viewingFriendProfile: null,
      });
      return;
    }

    this.setState({
      viewingFriendLoading: true,
      viewingFriendError: "",
    });

    try {
      const profile = await getPublicProfile(viewerUid, targetUid);
      this.setState({
        viewingFriendProfile: profile,
        viewingFriendLoading: false,
        viewingFriendError: "",
      });
    } catch (error) {
      console.error("Failed to load player profile:", error);
      this.setState({
        viewingFriendProfile: null,
        viewingFriendLoading: false,
        viewingFriendError:
          error?.message || "Could not load this player profile.",
      });
    }
  };

  schedulePublicProfileSync = () => {
    if (
      !this.state.isAuthenticated ||
      !this.state.user?.uid ||
      !isValidGid(this.state.gid)
    ) {
      return;
    }

    if (this.publicProfileSaveTimer) {
      clearTimeout(this.publicProfileSaveTimer);
    }

    this.publicProfileSaveTimer = setTimeout(() => {
      if (
        !this.state.isAuthenticated ||
        !this.state.user?.uid ||
        !isValidGid(this.state.gid)
      ) {
        return;
      }

      syncUserPublicProfile(this.state.user.uid, {
        gid: this.state.gid,
        username: this.getSafeUsername(this.state.username),
        profileImageId: this.state.profileImageId,
        gamesPlayed: this.state.gamesPlayed || 0,
        topScore: this.state.topScore || 0,
        collectionUnlocked: this.state.collectionUnlocked,
        collectionFoilUnlocked: this.state.collectionFoilUnlocked,
        endlessStats: this.state.endlessStats,
        storyProgress: this.state.storyProgress,
        cosmetics: this.state.cosmetics,
      }).catch((e) => console.error("Failed to sync public profile:", e));
    }, 600);
  };

  provisionGidAndPublicProfile = async () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    this.setState({ gidStatus: "loading", gidError: "" });

    try {
      const gid = await ensureUserGid(this.state.user.uid);
      this.setState({ gid, gidStatus: "ready", gidError: "" }, () => {
        this.schedulePublicProfileSync();
        this.refreshFriendsSocialState();
        this.syncLeaderboardBestsFromServer();
      });
    } catch (error) {
      console.error("Failed to ensure Game Identity:", error);
      this.setState({
        gidStatus: "error",
        gidError:
          error?.message ||
          "Could not set up your Game Identity. Please try again.",
      });
    }
  };

  queueLeaderboardSubmissions = (previousStats, nextStats) => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    const previousScores = buildLeaderboardScoresFromEndlessStats(previousStats);
    const nextScores = buildLeaderboardScoresFromEndlessStats(nextStats);
    const previousMap = new Map(
      previousScores.map((item) => [item.boardId, item.value])
    );

    nextScores.forEach((score) => {
      const previousValue = Number(previousMap.get(score.boardId)) || 0;
      if (score.value <= previousValue) {
        return;
      }

      submitLeaderboardScore(score.boardId, score.value).catch((error) =>
        console.error("Failed to submit leaderboard score:", error)
      );
    });
  };

  syncLeaderboardBestsFromServer = () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    setUserEndlessStats(this.state.user.uid, this.state.endlessStats)
      .catch((error) =>
        console.error(
          "Failed to save endless stats before leaderboard sync:",
          error
        )
      )
      .finally(() => {
        syncMyLeaderboardBests().catch((error) =>
          console.error("Failed to sync leaderboard bests:", error)
        );
      });
  };

  goLeaderboards = () => {
    if (
      !isEndlessHighScoreUnlocked(
        this.state.storyProgress || loadStoryProgress()
      )
    ) {
      return;
    }
    this.requestLeaveRun("leaderboards");
  };

  setLeaderboardCategory = (categoryId) => {
    this.setState({ leaderboardCategoryId: categoryId });
  };

  setLeaderboardBoardSize = (boardSize) => {
    this.setState({ leaderboardBoardSize: Number(boardSize) || 4 });
  };

  refreshLeaderboard = async () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      this.setState({
        leaderboardEntries: [],
        leaderboardMyEntry: null,
        leaderboardLoading: false,
        leaderboardError: "",
      });
      return;
    }

    const boardId = buildLeaderboardBoardId(
      this.state.leaderboardCategoryId,
      this.state.leaderboardBoardSize
    );

    if (!boardId) {
      this.setState({
        leaderboardError: "Invalid leaderboard board.",
        leaderboardLoading: false,
      });
      return;
    }

    this.setState({ leaderboardLoading: true, leaderboardError: "" });

    try {
      const { entries, myEntry } = await fetchLeaderboardBoard(
        boardId,
        this.state.user.uid
      );

      this.setState({
        leaderboardEntries: entries,
        leaderboardMyEntry: myEntry,
        leaderboardLoading: false,
        leaderboardError: "",
      });
    } catch (error) {
      console.error("Failed to load leaderboard:", error);
      this.setState({
        leaderboardLoading: false,
        leaderboardError:
          error?.message ||
          "Could not load leaderboard. Sign in and try again — if this keeps happening, the Open Beta server may still be updating.",
      });
    }
  };

  refreshFriendsSocialState = async () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      this.setState({
        friends: [],
        incomingFriendRequests: [],
        outgoingFriendRequests: [],
        friendsLoading: false,
        friendsError: "",
      });
      return;
    }

    this.setState({ friendsLoading: true, friendsError: "" });

    try {
      const social = await loadFriendsSocialState(this.state.user.uid);
      this.setState({
        friends: social.friends,
        incomingFriendRequests: social.incomingRequests,
        outgoingFriendRequests: social.outgoingRequests,
        friendsLoading: false,
        friendsError: "",
      });
    } catch (error) {
      console.error("Failed to load friends:", error);
      this.setState({
        friendsLoading: false,
        friendsError:
          error?.message ||
          "Could not load friends. Sign in and try again — if this keeps happening, the Open Beta server may still be updating.",
      });
    }
  };

  runFriendsAction = async (actionKey, actionFn, successMessage) => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return false;
    }

    this.setState({
      friendsBusyActionKey: actionKey,
      friendsActionError: "",
      friendsActionMessage: "",
    });

    try {
      await actionFn();
      await this.refreshFriendsSocialState();
      this.setState({
        friendsBusyActionKey: "",
        friendsActionMessage: successMessage || "",
        friendsActionError: "",
      });
      return true;
    } catch (error) {
      console.error("Friends action failed:", error);
      this.setState({
        friendsBusyActionKey: "",
        friendsActionMessage: "",
        friendsActionError:
          error?.message || "Something went wrong. Please try again.",
      });
      return false;
    }
  };

  handleSendFriendRequest = (gid) =>
    this.runFriendsAction(
      "send",
      () => sendFriendRequestByGid(gid),
      "Friend request sent."
    );

  handleAcceptFriendRequest = (requestId) =>
    this.runFriendsAction(
      `accept:${requestId}`,
      () => acceptFriendRequestById(requestId),
      "Friend request accepted."
    );

  handleDenyFriendRequest = (requestId) =>
    this.runFriendsAction(
      `deny:${requestId}`,
      () => denyFriendRequestById(requestId),
      "Friend request denied."
    );

  handleCancelFriendRequest = (requestId) =>
    this.runFriendsAction(
      `cancel:${requestId}`,
      () => cancelFriendRequestById(requestId),
      "Friend request canceled."
    );

  handleUnfriend = (friendUid) =>
    this.runFriendsAction(
      `unfriend:${friendUid}`,
      () => unfriendByUid(friendUid),
      "Removed from friends."
    );

  getCurrentRunElapsedMs = () => {
    if (!this.state.runStartedAt) {
      return this.state.runFinalElapsedMs || this.state.runElapsedMs || 0;
    }

    let pausedMs = this.state.runPauseAccumulatedMs || 0;
    if (this.state.runPausedAt) {
      pausedMs += Math.max(0, Date.now() - this.state.runPausedAt);
    }

    return Math.max(0, Date.now() - this.state.runStartedAt - pausedMs);
  };

  getBlitzRemainingMs = () => {
    return Math.max(0, BLITZ_DURATION_MS - this.getCurrentRunElapsedMs());
  };

  getFinishedRunTimingState = () => {
    const elapsedMs = this.getCurrentRunElapsedMs();

    return {
      runElapsedMs: elapsedMs,
      runFinalElapsedMs: elapsedMs,
    };
  };

  startRunTimer = () => {
    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
    }

    this.runTimerInterval = setInterval(this.tickRunTimer, RUN_TIMER_TICK_MS);
    this.tickRunTimer();
  };

  stopRunTimer = () => {
    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
      this.runTimerInterval = null;
    }
  };

  tickRunTimer = () => {
    if (
      this.state.screen !== "game" ||
      !this.state.runStartedAt ||
      this.state.showResumeOverlay ||
      this.state.runPausedAt
    ) {
      return;
    }

    const elapsedMs = this.getCurrentRunElapsedMs();

    if (this.state.runMode === "blitz" && elapsedMs >= BLITZ_DURATION_MS) {
      this.endRunFromBlitzTimeout();
      return;
    }

    this.setState({
      runElapsedMs: elapsedMs,
    });
  };

  endRunFromBlitzTimeout = () => {
    if (this.state.screen !== "game") {
      return;
    }

    this.stopRunTimer();
    this.clearAnxiousTimers();

    const runConfig = this.getActiveRunConfig();

    this.setState({
      ...this.getFinishedRunTimingState(),
      ...this.applyTreatGrant("loss", runConfig),
      screen: "recap",
      runOutcome: "loss",
      runEndReason: "blitz-timeout",
      runLevelReached: isStoryRun(runConfig)
        ? runConfig.storyLevelId || 1
        : this.state.currentLevel + 1,
      storyUnlockMessage: "",
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      showHeartEmptyOverlay: false,
      heartRewardBusy: "",
      heartRewardError: "",
      pendingWrongCardId: null,
      pendingNextLevel: null,
      forgivingMistakeMessage: "",
    }, this.saveTreatState);
  };

  goPlay = (input = {}) => {
    this.stopRunTimer();
    this.clearAnxiousTimers();

    let runConfig = null;

    if (input.runType === "story" || input.runType === "endless") {
      runConfig = normalizeRunConfig(input);
    } else if (input.storyLevelId) {
      runConfig = buildStoryRunConfig(input.storyLevelId);
    } else if (
      input.boardSize ||
      input.twistedMode ||
      input.shyCorgisMode ||
      input.anxiousMode ||
      input.hardcoreMode ||
      input.blitzMode
    ) {
      runConfig = buildEndlessRunConfig(input);
    } else {
      // Legacy difficulty adapter
      const startLevel = input.blitzMode ? 1 : input.startLevel || 1;
      const startIndex = Math.max(
        0,
        Math.min(this.levels.length - 1, startLevel - 1)
      );
      const boardSize = this.levels[startIndex].cards;

      runConfig = normalizeRunConfig({
        runType: "endless",
        boardSize,
        twistedMode: Boolean(input.twistedMode),
        shyCorgisMode: Boolean(input.shyCorgisMode),
        anxiousMode: Boolean(input.anxiousMode),
        hardcoreMode: Boolean(input.hardcoreMode),
        blitzMode: Boolean(input.blitzMode),
      });
      runConfig.legacyStartScore = input.blitzMode
        ? 0
        : Number(input.startScore) || 0;
    }

    if (runConfig && isStoryRun(runConfig) && isStoryHardModeActive(this.state.storyProgress)) {
      runConfig = applyStoryHardMode(runConfig);
    }

    if (!runConfig) {
      runConfig = buildEndlessRunConfig({ boardSize: 4 });
    }

    const blitzMode = Boolean(runConfig.blitzMode);
    const twistedMode = Boolean(runConfig.twistedMode);
    const shyCorgisMode = Boolean(runConfig.shyCorgisMode);
    const anxiousMode = Boolean(runConfig.anxiousMode);
    const hardcoreMode = blitzMode ? true : Boolean(runConfig.hardcoreMode);
    const cardsCount = runConfig.boardSize;
    const startScore =
      isStoryRun(runConfig) || blitzMode
        ? 0
        : Number(runConfig.legacyStartScore) || 0;

    const startingCards = this.prepareCardsForBoard(
      this.getNewCards(cardsCount, runConfig),
      twistedMode,
      runConfig?.tutorialBehavior?.twistedCardLimit || 0,
      isStoryRun(runConfig) && twistedMode,
      runConfig
    );

    const isStory = isStoryRun(runConfig);

    if (!isStory) {
      this.markEndlessPlayed();
    }

    this.setState(
      this.buildBoardCardsStatePatch(startingCards, {
        showLevelOverlay: false,
        isBoardVisible: true,
        showLevelCompleteOverlay: false,
        showHeartContinueOverlay: false,
        showHeartEmptyOverlay: false,
        heartRewardBusy: "",
        heartRewardError: "",
        pendingNextLevel: null,
        pendingWrongCardId: null,

        screen: "game",
        screenHistory: this.buildScreenHistory(
          this.state.screen,
          "game",
          this.state.screenHistory
        ),
        twistedMode,
        shyCorgisMode,
        anxiousMode,
        anxiousWarning: false,
        shyCorgisFlipLocked: false,
        hardcoreMode,
        shyHiddenCardIds: [],
        gamesPlayed: (this.state.gamesPlayed || 0) + 1,

        runCorrectClicks: 0,
        runLevelsCompleted: 0,
        runFoilsFound: 0,
        runHiddenBoostsFound: 0,
        runHiddenPacksFound: 0,
        runNewUnlocks: 0,
        runTreatsEarned: 0,
        runBoostBundlesEarned: 0,
        runOutcome: "loss",
        earnedRewardOffers: [],
        doubledEarnedRewardIds: [],
        earnedRewardDoubleBusy: "",
        earnedRewardDoubleError: "",
        runLevelReached: isStory ? runConfig.storyLevelId || 1 : 1,
        runMode: blitzMode ? "blitz" : "normal",
        runConfig: {
          ...runConfig,
          hardcoreMode,
          blitzMode,
          anxiousMode,
        },
        // Story waits for the intro popup before the run timer starts.
        runStartedAt: isStory ? null : Date.now(),
        runElapsedMs: 0,
        runFinalElapsedMs: 0,
        runEndReason: "",
        showResumeOverlay: false,
        runPausedAt: null,
        pauseReason: "",
        runPauseAccumulatedMs: 0,
        storyUnlockMessage: "",
        storyClearsCompleted: 0,
        showStoryIntro: isStory,
        storyBoardDealKey: 0,
        storyAlertKind: "",
        storyGuideStep: null,
        storyGuideHighlightId: null,
        storyGuideWrongLessonDone: false,
        showGuideHeartDemo: false,
        showShinyDiscovery: false,
        hiddenLootDiscoveryKind: null,
        score: 0,
        endlessProgressClicks: isStory ? 0 : startScore,
        topScore: this.state.topScore,
        clicked: Array(cardsCount).fill(false),
        currentLevel: 0,
        currentPerfectStreak: 0,
        runHeartsSpent: 0,
        usedCards: [],
        forgivingMistakeMessage: "",
        ...createEmptyRunBoostState(),
      }),
      () => {
        if (!isStory) {
          this.startRunTimer();
          this.scheduleAnxiousScramble();
        }
      }
    );
  };

  getAdminLevelContext = () => {
    const runConfig = this.getActiveRunConfig();

    if (
      (this.state.screen === "game" && isStoryRun(runConfig)) ||
      this.state.screen === "story"
    ) {
      const currentId =
        this.state.screen === "game" && isStoryRun(runConfig)
          ? Number(runConfig.storyLevelId) || 1
          : Number(this.state.storyProgress?.highestUnlockedLevelId) || 1;

      return {
        mode: "story",
        currentId: Math.max(1, Math.min(STORY_LEVELS.length, currentId)),
        maxId: STORY_LEVELS.length,
      };
    }

    return {
      mode: "ladder",
      currentId: this.state.currentLevel + 1,
      maxId: this.levels.length,
    };
  };

  adminGoToLevel = (levelNumber) => {
    const context = this.getAdminLevelContext();

    if (context.mode === "story") {
      const safeStoryLevel = Math.max(
        1,
        Math.min(STORY_LEVELS.length, Number(levelNumber) || 1)
      );
      const storyConfig = buildStoryRunConfig(safeStoryLevel);

      if (storyConfig) {
        this.goPlay(storyConfig);
      }

      return;
    }

    const safeLevel = Math.max(1, Math.min(this.levels.length, levelNumber));

    this.goPlay({
      startLevel: safeLevel,
      startScore: this.levels[safeLevel - 2]?.threshold || 0,
    });
  };

  adminGoToPreviousLevel = () => {
    const context = this.getAdminLevelContext();
    this.adminGoToLevel(context.currentId - 1);
  };

  adminGoToNextLevel = () => {
    const context = this.getAdminLevelContext();
    this.adminGoToLevel(context.currentId + 1);
  };

  adminRefreshBoard = () => {
    const currentLevelConfig = this.getLevelConfig(this.state.currentLevel);
    const refreshedCards = this.prepareCardsForBoard(
      this.getNewCards(currentLevelConfig.cards),
      this.state.twistedMode
    );

    this.setState(
      this.buildBoardCardsStatePatch(refreshedCards, {
        clicked: Array(currentLevelConfig.cards).fill(false),
        showLevelCompleteOverlay: false,
        showStoryIntro: false,
        showGuideHeartDemo: false,
        storyGuideStep: null,
        storyGuideHighlightId: null,
        pendingNextLevel: null,
        isBoardVisible: true,
      })
    );
  };

  stopAdminAutoComplete = () => {
    if (this.adminAutoCompleteTimer) {
      clearTimeout(this.adminAutoCompleteTimer);
      this.adminAutoCompleteTimer = null;
    }

    this.adminAutoCompleteSession = null;
    this.setState({ adminAutoCompleting: false });
  };

  adminCompleteLevel = () => {
    if (this.state.screen !== "game" || this.adminAutoCompleteSession?.active) {
      return;
    }

    const startAutoPlay = () => {
      this.adminAutoCompleteSession = {
        active: true,
        knownClicked: new Set(
          (this.state.clicked || [])
            .filter((entry) => entry !== false)
            .map((entry) => String(normalizeId(entry)))
        ),
        lastDealKey: this.state.storyBoardDealKey || 0,
        safetyTicks: 0,
      };

      this.setState({ adminAutoCompleting: true }, () => {
        this.adminAutoCompleteTick();
      });
    };

    this.setState(
      {
        showStoryIntro: false,
        showGuideHeartDemo: false,
        showHeartContinueOverlay: false,
        showHeartEmptyOverlay: false,
        heartRewardBusy: "",
        heartRewardError: "",
        pendingWrongCardId: null,
        storyGuideStep: null,
        storyGuideHighlightId: null,
        storyGuideWrongLessonDone: true,
        storyAlertKind: "",
        forgivingMistakeMessage: "Admin auto-complete running…",
        runStartedAt: this.state.runStartedAt || Date.now(),
      },
      () => {
        if (!this.runTimerInterval) {
          this.startRunTimer();
        }
        startAutoPlay();
      }
    );
  };

  adminAutoCompleteTick = () => {
    const session = this.adminAutoCompleteSession;

    if (!session?.active) {
      return;
    }

    if (this.state.screen !== "game") {
      this.stopAdminAutoComplete();
      return;
    }

    if (this.state.showLevelCompleteOverlay) {
      this.stopAdminAutoComplete();
      return;
    }

    session.safetyTicks += 1;
    if (session.safetyTicks > 240) {
      this.stopAdminAutoComplete();
      this.setState({
        forgivingMistakeMessage: "Admin auto-complete stopped (safety limit).",
        storyAlertKind: "perfect-reset",
      });
      return;
    }

    const dealKey = this.state.storyBoardDealKey || 0;
    if (dealKey !== session.lastDealKey) {
      session.lastDealKey = dealKey;
      session.knownClicked = new Set();
    }

    const boardCards = this.state.cards || [];
    const remaining = boardCards.filter(
      (card) =>
        !session.knownClicked.has(String(normalizeId(card.id))) &&
        !this.findId(card.id)
    );

    if (remaining.length === 0) {
      // Wait for a clear/reset, then continue picking the next board.
      session.knownClicked = new Set(
        (this.state.clicked || [])
          .filter((entry) => entry !== false)
          .map((entry) => String(normalizeId(entry)))
      );
      this.adminAutoCompleteTimer = setTimeout(this.adminAutoCompleteTick, 420);
      return;
    }

    const nextCard = remaining[0];
    const nextId = String(normalizeId(nextCard.id));
    session.knownClicked.add(nextId);

    this.handleClick({
      target: { id: String(nextCard.id) },
      currentTarget: {
        dataset: { foil: nextCard.isFoil ? "true" : "false" },
      },
    });

    this.adminAutoCompleteTimer = setTimeout(this.adminAutoCompleteTick, 380);
  };

  adminToggleUnlockAll = (event) => {
    const enabled = event.target.checked;

    if (enabled) {
      const allIds = characters.map((c) => normalizeId(c.id));

      setMaxUnlockedLevel(this.levels.length);
      markBaseGameCompleted();

      const normalizedProgress = saveStoryProgress(
        buildAdminUnlockAllStoryProgress()
      );
      const nextEndlessStats = buildAdminUnlockAllEndlessStats();
      const nextTopScore = Math.max(
        Number(this.state.topScore) || 0,
        ADMIN_UNLOCK_HIGH_SCORE
      );
      const nextGamesPlayed = Math.max(
        Number(this.state.gamesPlayed) || 0,
        ADMIN_UNLOCK_GAMES_PLAYED
      );
      const unlockedCosmetics = unlockAllGrantableCosmetics(
        this.state.cosmetics
      ).state;
      const nextCosmetics = this.syncCosmeticsWithStoryProgress(
        unlockedCosmetics,
        normalizedProgress,
        allIds,
        nextEndlessStats
      );

      this.setState(
        {
          adminUnlockAll: true,
          storyProgress: normalizedProgress,
          collectionUnlocked: allIds,
          collectionFoilUnlocked: allIds,
          cosmetics: nextCosmetics,
          endlessStats: nextEndlessStats,
          topScore: nextTopScore,
          gamesPlayed: nextGamesPlayed,
        },
        () => {
          saveEndlessStats(nextEndlessStats);
          if (this.state.isAuthenticated && this.state.user?.uid) {
            setUserStoryProgress(this.state.user.uid, normalizedProgress).catch(
              (e) => console.error("Failed to save story progress:", e)
            );
            setUserCosmetics(this.state.user.uid, nextCosmetics).catch((e) =>
              console.error("Failed to save cosmetics:", e)
            );
            setUserEndlessStats(this.state.user.uid, nextEndlessStats).catch(
              (e) => console.error("Failed to save endless stats:", e)
            );
          }
        }
      );

      return;
    }

    this.setState({ adminUnlockAll: false });
  };

  adminResetAllContent = () => {
      const emptyIds = [];
      const emptyStoryProgress = createEmptyStoryProgress();
      const emptyEndlessStats = createEmptyEndlessStats();
      const emptyCosmetics = createEmptyCosmeticsState();

      try {
          localStorage.removeItem(LS_COLLECTION_KEY);
          localStorage.removeItem(LS_FOIL_COLLECTION_KEY);
          localStorage.removeItem(LS_TREATS_KEY);
          localStorage.setItem("mmu_max_level", "1");
          localStorage.setItem(LS_GAMES_PLAYED_KEY, "0");
          clearBaseGameCompleted();
          saveStoryProgress(emptyStoryProgress);
          saveEndlessStats(emptyEndlessStats);
          saveCosmeticsState(emptyCosmetics);
      } catch {
          // ignore
      }

      this.setState({
          adminUnlockAll: false,
          collectionUnlocked: emptyIds,
          collectionFoilUnlocked: emptyIds,
          treats: 0,
          topScore: 0,
          gamesPlayed: 0,
          storyProgress: emptyStoryProgress,
          endlessStats: emptyEndlessStats,
          cosmetics: emptyCosmetics,
      });

      if (this.state.isAuthenticated && this.state.user?.uid) {
          setUserCollection(this.state.user.uid, emptyIds).catch((e) =>
              console.error("Failed to reset collection:", e)
          );

          setUserFoilCollection(this.state.user.uid, emptyIds).catch((e) =>
              console.error("Failed to reset foil collection:", e)
          );

          setUserTreats(this.state.user.uid, 0).catch((e) =>
              console.error("Failed to reset treats:", e)
          );

          setUserHighScore(this.state.user.uid, 0).catch((e) =>
              console.error("Failed to reset high score:", e)
          );

          setUserStoryProgress(this.state.user.uid, emptyStoryProgress).catch(
              (e) => console.error("Failed to reset story progress:", e)
          );

          setUserEndlessStats(this.state.user.uid, emptyEndlessStats).catch(
              (e) => console.error("Failed to reset endless stats:", e)
          );

          setUserCosmetics(this.state.user.uid, emptyCosmetics).catch((e) =>
              console.error("Failed to reset cosmetics:", e)
          );
      }
  };

  handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      this.syncNativeBillingAccount(null);
      this.goHome();
      this.setState({
        isAuthenticated: false,
        user: null,
        gid: null,
        gidStatus: "idle",
        gidError: "",
        friends: [],
        incomingFriendRequests: [],
        outgoingFriendRequests: [],
        friendsLoading: false,
        friendsError: "",
        friendsActionMessage: "",
        friendsActionError: "",
        friendsBusyActionKey: "",
        viewingFriendUid: null,
        viewingFriendProfile: null,
        viewingFriendLoading: false,
        viewingFriendError: "",
        redemptions: createEmptyRedemptionState(),
        storeRedeemBusy: false,
        storeRedeemMessage: "",
      });
    }
  };

  renderUnlockToast = () => {
    return null;
  };

  renderPersistentStoreButton = () => {
    return null;
  };

  renderPageMenu = () => {
    const storeUnlocked = isStoreUnlocked(
      this.state.storyProgress || loadStoryProgress()
    );

    return (
      <OverlayTopBar
        variant="fixed"
        onHome={this.goHome}
        onProfile={this.goProfile}
        onStore={this.goStore}
        onTreats={this.goStoreTreats}
        onBack={this.goBack}
        storeUnlocked={storeUnlocked}
        treats={this.state.treats}
      />
    );
  };

  renderAdminPanel = () => {
    if (
      !canAccessAdminPanel(this.state.user, { enabled: ENABLE_ADMIN_PANEL })
    ) {
      return null;
    }

    const adminLevel = this.getAdminLevelContext();
    const levelLabel =
      adminLevel.mode === "story" ? "Story Level" : "Current Level";
    const isOpen = Boolean(this.state.adminPanelOpen);

    return (
      <>
        <button
          type="button"
          className="adminPanelToggle"
          aria-expanded={isOpen}
          aria-controls="mmu-admin-panel"
          onClick={() =>
            this.setState((prev) => ({
              adminPanelOpen: !prev.adminPanelOpen,
            }))
          }
        >
          {isOpen ? "Close Admin" : "Admin"}
        </button>

        {isOpen ? (
          <div
            id="mmu-admin-panel"
            className="adminPanel"
            role="dialog"
            aria-label="Admin Panel"
          >
            <div className="adminPanel__header">
              <div className="adminPanel__title">Admin Panel</div>
              <button
                type="button"
                className="adminPanel__close"
                aria-label="Close admin panel"
                onClick={() => this.setState({ adminPanelOpen: false })}
              >
                ×
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={this.adminGoToPreviousLevel}
                disabled={adminLevel.currentId <= 1}
              >
                Previous Level
              </button>

              <button
                className="adminPanel__button"
                onClick={this.adminGoToNextLevel}
                disabled={adminLevel.currentId >= adminLevel.maxId}
              >
                Next Level
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={this.adminRefreshBoard}
                disabled={this.state.screen !== "game"}
              >
                Refresh Board
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={this.adminCompleteLevel}
                disabled={
                  this.state.screen !== "game" || this.state.adminAutoCompleting
                }
              >
                {this.state.adminAutoCompleting
                  ? "Completing…"
                  : "Complete Level"}
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={() =>
                  this.setState(
                    {
                      treats: grantTreats(this.state.treats, ADMIN_TREATS_GRANT),
                    },
                    this.saveTreatState
                  )
                }
              >
                +{ADMIN_TREATS_GRANT.toLocaleString("en-US")} Treats
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={() =>
                  this.setState(
                    {
                      inventory: grantAllBoostItems(
                        this.state.inventory,
                        ADMIN_BOOST_GRANT_EACH
                      ),
                    },
                    this.saveInventoryState
                  )
                }
              >
                +{ADMIN_BOOST_GRANT_EACH} each Boost
              </button>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button"
                onClick={() => notifyScreenshotTaken()}
              >
                Simulate screenshot
              </button>
            </div>

            <div className="adminPanel__row">
              <label className="adminPanel__label">
                <input
                  type="checkbox"
                  checked={this.state.adminUnlockAll}
                  onChange={this.adminToggleUnlockAll}
                />
                Unlock all content
              </label>
            </div>

            <div className="adminPanel__row">
              <button
                className="adminPanel__button adminPanel__button--danger"
                onClick={this.adminResetAllContent}
              >
                Reset / Lock All Content
              </button>
            </div>

            <div className="adminPanel__meta">
              {levelLabel}: {adminLevel.currentId}
              {adminLevel.mode === "story" ? ` / ${adminLevel.maxId}` : ""}
            </div>
          </div>
        ) : null}
      </>
    );
  };

  // path: src/App.js

  syncGameBodyClass = () => {
    document.body.classList.toggle(
      "gameBoardActive",
      this.state.screen === "game"
    );
    document.body.classList.toggle(
      "recapActive",
      this.state.screen === "recap"
    );
  };

  generateDefaultUsername = () => {
    const randomNumber = Math.floor(100000000 + Math.random() * 900000000);
    return `user${randomNumber}`;
  };

  getSafeUsername = (value) => {
    const cleaned = String(value || "").trim();

    if (
      !cleaned ||
      cleaned.toLowerCase() === "null" ||
      cleaned.toLowerCase() === "undefined" ||
      !isUsernameAllowed(cleaned)
    ) {
      return this.generateDefaultUsername();
    }

    return cleaned.slice(0, 24);
  };

  saveGuestUsername = () => {
    try {
      localStorage.setItem(
        LS_USERNAME_KEY,
        this.getSafeUsername(this.state.username)
      );
    } catch {
      // ignore
    }
  };

  changeUsername = (nextUsername) => {
    const cleaned = String(nextUsername || "").trim();

    if (!isUsernameAllowed(cleaned)) {
      return false;
    }

    this.setState({
      username: this.getSafeUsername(cleaned),
    });

    return true;
  };

  render() {
    return (
      <>
        {this.renderPersistentStoreButton()}
        {this.renderScreen()}
        <InterstitialAdPreviewModal
          open={this.state.showInterstitialAdPreview}
          onComplete={this.handleInterstitialAdPreviewComplete}
        />
      </>
    );
  }

  renderScreen() {
    if (this.state.screen === "home") {
      return (
        <>
          {this.renderUnlockToast()}
          {this.renderAdminPanel()}

          <Home
            onStoryMode={this.goStoryMode}
            onEndlessMode={this.goEndlessMode}
            onProfile={this.goProfile}
            onFriends={this.goFriends}
            onLeaderboards={this.goLeaderboards}
            onStore={this.goStore}
            onHelp={this.goHelp}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
            onLoginSuccess={(user) => this.setState({ user, isAuthenticated: true })}
            onLogout={this.handleLogout}
            isAuthenticated={this.state.isAuthenticated}
            user={this.state.user}
            username={this.state.username}
            storyProgress={this.state.storyProgress}
          />
        </>
      );
    }

    if (this.state.screen === "legal") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Legal
            docId={this.state.legalDocId || "privacy"}
            onBack={this.goBack}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
          />
        </>
      );
    }

    if (this.state.screen === "help") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Help
            onBack={this.goBack}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
          />
        </>
      );
    }

    if (this.state.screen === "store") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Store
            isAuthenticated={this.state.isAuthenticated}
            hearts={this.state.hearts}
            maxHearts={MAX_HEARTS}
            treats={this.state.treats}
            packBusy={this.state.storePackBusy}
            packResult={this.state.storePackResult}
            remainingInventoryPacks={getCorgiPackCount(this.state.inventory)}
            adsRemoved={this.isAdsRemoved()}
            busyProductId={this.state.storeBusyProductId}
            redeemBusy={this.state.storeRedeemBusy}
            redeemMessage={this.state.storeRedeemMessage}
            error={this.state.storeError}
            initialTab={this.state.storeInitialTab || "featured"}
            onPurchaseProduct={this.handlePurchaseStoreProduct}
            onSpendTreats={this.handleSpendTreatsShop}
            onRedeemCode={this.handleRedeemStoreCode}
            onClosePackReveal={this.handleClosePackReveal}
            onOpenNextPack={this.handleOpenNextInventoryPack}
            onRestorePurchases={this.handleRestoreStorePurchases}
          />

          {this.renderPackOverlays()}

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "leaderboards") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Leaderboards
            isAuthenticated={this.state.isAuthenticated}
            user={this.state.user}
            entries={this.state.leaderboardEntries}
            myEntry={this.state.leaderboardMyEntry}
            loading={this.state.leaderboardLoading}
            error={this.state.leaderboardError}
            categoryId={this.state.leaderboardCategoryId}
            boardSize={this.state.leaderboardBoardSize}
            onChangeCategory={this.setLeaderboardCategory}
            onChangeBoardSize={this.setLeaderboardBoardSize}
            onRefresh={this.refreshLeaderboard}
            onLoginSuccess={(user) =>
              this.setState({ user, isAuthenticated: true })
            }
            onViewProfile={this.goFriendProfile}
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "friends") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Friends
            isAuthenticated={this.state.isAuthenticated}
            user={this.state.user}
            gid={this.state.gid}
            gidStatus={this.state.gidStatus}
            gidError={this.state.gidError}
            incomingRequests={this.state.incomingFriendRequests}
            outgoingRequests={this.state.outgoingFriendRequests}
            friends={this.state.friends}
            friendsLoading={this.state.friendsLoading}
            friendsError={this.state.friendsError}
            actionMessage={this.state.friendsActionMessage}
            actionError={this.state.friendsActionError}
            busyActionKey={this.state.friendsBusyActionKey}
            onLoginSuccess={(user) =>
              this.setState({ user, isAuthenticated: true })
            }
            onRetryGid={this.provisionGidAndPublicProfile}
            onRefreshFriends={this.refreshFriendsSocialState}
            onSendFriendRequest={this.handleSendFriendRequest}
            onAcceptFriendRequest={this.handleAcceptFriendRequest}
            onDenyFriendRequest={this.handleDenyFriendRequest}
            onCancelFriendRequest={this.handleCancelFriendRequest}
            onUnfriend={this.handleUnfriend}
            onViewFriendProfile={this.goFriendProfile}
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "friendProfile") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <FriendProfile
            profile={this.state.viewingFriendProfile}
            loading={this.state.viewingFriendLoading}
            error={this.state.viewingFriendError}
            totalCards={characters.length}
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "story") {
      return (
        <>
          {this.renderUnlockToast()}
          {this.renderAdminPanel()}

          <StoryMode
            progress={this.state.storyProgress}
            onPlayStoryLevel={this.goPlay}
            onToggleHardMode={this.setStoryHardMode}
            onBackHome={this.goBack}
            onHome={this.goHome}
            onProfile={this.goProfile}
            onStore={this.goStore}
            onTreats={this.goStoreTreats}
            storeUnlocked={isStoreUnlocked(
              this.state.storyProgress || loadStoryProgress()
            )}
            treats={this.state.treats}
            initialMapPage={this.state.storyMapPage || "surface"}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
          />
        </>
      );
    }

    if (this.state.screen === "endless") {
      return (
        <>
          {this.renderUnlockToast()}
          {this.renderAdminPanel()}

          <EndlessMode
            progress={this.state.storyProgress}
            onStartEndless={this.goPlay}
            onBackHome={this.goBack}
            onHome={this.goHome}
            onProfile={this.goProfile}
            onStore={this.goStore}
            onTreats={this.goStoreTreats}
            onLeaderboards={this.goLeaderboards}
            storeUnlocked={isStoreUnlocked(
              this.state.storyProgress || loadStoryProgress()
            )}
            treats={this.state.treats}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
          />
        </>
      );
    }

    if (this.state.screen === "profile") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Profile
            username={this.state.username}
            onChangeUsername={this.changeUsername}
            unlockedIds={this.state.collectionUnlocked}
            selectedProfileImageId={this.state.profileImageId}
            equippedCosmetics={getEquippedCosmetics(this.state.cosmetics)}
            onCosmetics={this.goCosmetics}
            onCollection={this.goCollection}
            onChooseProfileImage={this.goChooseProfileImage}
            onAchievements={this.goAchievements}
            onFriends={this.goFriends}
            inventoryEntries={listOwnedInventoryEntries(this.state.inventory)}
            onOpenCorgiPacks={this.openInventoryCorgiPacks}
            cardsOwned={(this.state.collectionUnlocked || []).length}
            treats={this.state.treats}
            gamesPlayed={this.state.gamesPlayed || 0}
            foilsFound={(this.state.collectionFoilUnlocked || []).length}
            highestScore={this.state.topScore || 0}
            highestLevel={Math.max(
              0,
              ...Object.values(
                this.state.endlessStats?.bestLevelByBoardSize || {}
              ).map((value) => Number(value) || 0)
            )}
            longestStreak={
              Number(this.state.endlessStats?.bestPerfectStreak) || 0
            }
            isAuthenticated={this.state.isAuthenticated}
            gid={this.state.gid}
            gidStatus={this.state.gidStatus}
            gidError={this.state.gidError}
            onRetryGid={this.provisionGidAndPublicProfile}
          />

          {this.renderPackOverlays()}

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "collection") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Collection
            unlockedIds={this.state.collectionUnlocked}
            foilUnlockedIds={this.state.collectionFoilUnlocked}
            selectedProfileImageId={this.state.profileImageId}
            pickProfile={Boolean(this.state.collectionPickProfile)}
            onSelectProfileImage={this.selectProfileImageFromCollection}
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "achievements") {
      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Achievements
            collectionUnlocked={this.state.collectionUnlocked}
            collectionFoilUnlocked={this.state.collectionFoilUnlocked}
            storyProgress={this.state.storyProgress}
            endlessStats={this.state.endlessStats}
            topScore={this.state.topScore}
            gamesPlayed={this.state.gamesPlayed}
            totalCards={characters.length}
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "cosmetics") {
      const previewCard =
        characters.find(
          (card) =>
            normalizeId(card.id) === normalizeId(this.state.profileImageId)
        ) || null;

      return (
        <>
          {this.renderPageMenu()}
          {this.renderAdminPanel()}

          <Cosmetics
            cosmetics={this.state.cosmetics}
            unlockedCardIds={this.state.collectionUnlocked || []}
            onEquipCosmetic={this.handleEquipCosmetic}
            onUnequipCosmeticType={this.handleUnequipCosmeticType}
            previewImageSrc={
              previewCard ? getCardImageSrc(previewCard) : null
            }
            previewImageAlt={
              previewCard
                ? `${previewCard.name} profile`
                : "Profile preview"
            }
          />

          <BottomNav className="bottomNav">
            {this.renderAppFooter()}
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "recap") {
      const runConfig = this.getActiveRunConfig();
      const currentStoryLevelId = Number(runConfig.storyLevelId);
      const nextStoryLevel =
        runConfig.runType === "story" &&
        this.state.runOutcome === "win" &&
        Number.isFinite(currentStoryLevelId)
          ? getStoryLevelById(currentStoryLevelId + 1)
          : null;

      return (
        <>
          <Recap
            outcome={this.state.runOutcome}
            levelReached={this.state.runLevelReached}
            correctClicks={this.state.runCorrectClicks}
            foilsFound={this.state.runFoilsFound}
            hiddenBoostsFound={this.state.runHiddenBoostsFound}
            hiddenPacksFound={this.state.runHiddenPacksFound}
            newlyUnlocked={this.state.runNewUnlocks}
            treatsEarned={this.state.runTreatsEarned}
            boostBundlesEarned={this.state.runBoostBundlesEarned}
            treatBalance={this.state.treats}
            totalUnlocked={(this.state.collectionUnlocked || []).length}
            totalCards={characters.length}
            runMode={this.state.runMode}
            runType={runConfig.runType}
            storyLevelId={runConfig.storyLevelId}
            nextStoryLevelId={nextStoryLevel ? nextStoryLevel.id : null}
            runEndReason={this.state.runEndReason}
            unlockMessage={this.state.storyUnlockMessage}
            runElapsedText={formatRunDuration(
              this.state.runFinalElapsedMs || this.state.runElapsedMs
            )}
            score={this.state.score || 0}
            bestScore={this.state.topScore || 0}
            showScore={isEndlessRun(runConfig)}
            showBest={
              isEndlessRun(runConfig) && this.isHighScoreEnabled()
            }
            hearts={this.state.hearts}
            heartLastRefillAt={this.state.heartLastRefillAt}
            maxHearts={MAX_HEARTS}
            hardcoreMode={Boolean(
              runConfig.hardcoreMode || this.state.hardcoreMode
            )}
            heartRewardBusy={this.state.heartRewardBusy}
            heartRewardError={this.state.heartRewardError}
            adStatus={getRewardedHeartAdStatus(Date.now(), {
              adsRemoved: this.isAdsRemoved(),
            })}
            adsRemoved={this.isAdsRemoved()}
            onWatchAd={this.handleWatchAdForHeart}
            onBuyRefill={this.handlePurchaseHeartRefill}
            earnedRewardOffers={this.state.earnedRewardOffers}
            doubledEarnedRewardIds={this.state.doubledEarnedRewardIds}
            rewardDoubleAdStatus={getRewardedDoubleAdStatus(Date.now(), {
              adsRemoved: this.isAdsRemoved(),
            })}
            earnedRewardDoubleBusy={this.state.earnedRewardDoubleBusy}
            earnedRewardDoubleError={this.state.earnedRewardDoubleError}
            onWatchAdForDouble={this.handleWatchAdForEarnedRewardDouble}
            onPlayAgain={this.goPlay}
            onStoryMode={this.goStoryMode}
            onEndlessMode={this.goEndlessMode}
            onContinueStory={this.continueNextStoryLevel}
            onMainMenu={this.goHome}
            onPrivacyPolicy={this.goPrivacyPolicy}
            onTermsOfService={this.goTermsOfService}
            banner={this.renderUnlockToast("aboveBoard")}
          />

          {this.renderPackOverlays()}

          <ReviewPromptModal
            open={Boolean(this.state.showReviewPrompt)}
            onRate={() => {
              const next = saveReviewPromptState({
                ...loadReviewPromptState(),
                completed: true,
                shownCount: (loadReviewPromptState().shownCount || 0) + 1,
              });
              this.setState({ showReviewPrompt: false });
              // Native store review APIs will plug in here (iOS/Android).
              void next;
            }}
            onDismiss={() => {
              saveReviewPromptState({
                ...loadReviewPromptState(),
                dismissed: true,
                shownCount: (loadReviewPromptState().shownCount || 0) + 1,
              });
              this.setState({ showReviewPrompt: false });
            }}
          />

          <RewardedAdPreviewModal
            open={this.state.showRewardedAdPreview}
            rewardLabel={this.state.rewardedAdPreviewLabel || "+1 Heart"}
            onComplete={this.handleRewardedAdPreviewComplete}
            onCancel={this.handleRewardedAdPreviewCancel}
          />
        </>
      );
    }

    const totalCards = this.state.cards.length;
    const cardsPerRow = Math.min(4, Math.ceil(Math.sqrt(totalCards)));
    document.documentElement.style.setProperty("--cards-per-row", cardsPerRow);

    return (
      <>
        {this.state.showLevelCompleteOverlay && (
          <div className="levelCompleteOverlay" role="dialog" aria-modal="true">
            <div className="levelCompleteOverlay__panel">
              <div className="levelCompleteOverlay__title">
                {isStoryRun(this.getActiveRunConfig())
                  ? `Story Level ${
                      this.getActiveRunConfig().storyLevelId || 1
                    } Complete!`
                  : `Level ${this.state.currentLevel + 1} Complete!`}
              </div>

              {this.isFinalMainLevelCompleteChoice() ? (
                <>
                  <div className="levelCompleteOverlay__message">
                    You completed the main run. Finish now or continue into Endless Mode.
                  </div>

                  <div className="levelCompleteOverlay__actions">
                    <button
                      className="levelCompleteOverlay__button"
                      onClick={this.finishRun}
                    >
                      Finish
                    </button>

                    <button
                      className="levelCompleteOverlay__button"
                      onClick={this.startNextLevel}
                    >
                      Endless
                    </button>
                  </div>
                </>
              ) : (
                <button
                  className="levelCompleteOverlay__button"
                  onClick={this.startNextLevel}
                >
                  {this.state.pendingNextLevel >= MAX_ENDLESS_LEVEL
                    ? "View Recap"
                    : `Start Level ${
                        (this.state.pendingNextLevel ?? this.state.currentLevel + 1) + 1
                      }`}
                </button>
              )}
            </div>
          </div>
        )}

        {this.state.forgivingMistakeMessage ? (
          <div
            className={`forgivingMistakeBanner${
              this.state.storyAlertKind
                ? ` forgivingMistakeBanner--${this.state.storyAlertKind}`
                : ""
            }${
              this.shouldCoverGameBarForStoryPrompt()
                ? " forgivingMistakeBanner--coversGameBar"
                : ""
            }`}
            role="status"
            aria-live="polite"
          >
            {this.state.forgivingMistakeMessage}
          </div>
        ) : null}

        {this.state.showHeartContinueOverlay && (
          <div className="heartContinueOverlay" role="dialog" aria-modal="true">
            <div className="heartContinueOverlay__panel">
              <div className="heartContinueOverlay__title">Wrong card!</div>

              <div className="heartContinueOverlay__message">
                {this.isHouseHeartContinueActive()
                  ? "Normally you'd spend 1 heart to keep going. This stage's continue is on the house."
                  : "Spend 1 heart to continue this run?"}
              </div>

              <div className="heartContinueOverlay__hearts">
                Hearts: {formatHeartsMeter(this.state.hearts)}
                {this.isHouseHeartContinueActive() ? " (unchanged)" : ""}
              </div>

              <div className="heartContinueOverlay__actions">
                <button
                  type="button"
                  className="levelCompleteOverlay__button"
                  onClick={this.spendHeartToContinue}
                  disabled={
                    !this.isHouseHeartContinueActive() && this.state.hearts <= 0
                  }
                >
                  {this.isHouseHeartContinueActive()
                    ? "Continue (on the house)"
                    : "Spend Heart"}
                </button>

                {!this.isHouseHeartContinueActive() &&
                this.state.hearts <= 0 ? (
                  <button
                    type="button"
                    className="levelCompleteOverlay__button"
                    onClick={() => this.openHeartEmptyOverlay()}
                  >
                    Get Hearts
                  </button>
                ) : null}

                <button
                  type="button"
                  className="levelCompleteOverlay__button"
                  onClick={this.declineHeartContinue}
                >
                  End Run
                </button>
              </div>
            </div>
          </div>
        )}

        {this.state.showHeartEmptyOverlay && !this.state.showRewardedAdPreview && (
          <HeartEmptyOverlay
            hearts={this.state.hearts}
            heartLastRefillAt={this.state.heartLastRefillAt}
            maxHearts={MAX_HEARTS}
            refillMs={HEART_REFILL_MS}
            busyAction={this.state.heartRewardBusy}
            error={this.state.heartRewardError}
            adStatus={getRewardedHeartAdStatus(Date.now(), {
              adsRemoved: this.isAdsRemoved(),
            })}
            adsRemoved={this.isAdsRemoved()}
            onWatchAd={this.handleWatchAdForHeart}
            onBuyProduct={this.handlePurchaseHeartsToContinue}
            onEndRun={this.declineHeartEmpty}
          />
        )}

        {this.renderPackOverlays()}

        <RewardedAdPreviewModal
          open={this.state.showRewardedAdPreview}
          rewardLabel={this.state.rewardedAdPreviewLabel || "+1 Heart"}
          onComplete={this.handleRewardedAdPreviewComplete}
          onCancel={this.handleRewardedAdPreviewCancel}
        />

        {this.state.showGuideHeartDemo && (
          <div
            className="guideHeartDemoOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guideHeartDemoTitle"
          >
            <div className="guideHeartDemoOverlay__panel">
              <div
                id="guideHeartDemoTitle"
                className="guideHeartDemoOverlay__title"
              >
                Wrong card!
              </div>
              <p className="guideHeartDemoOverlay__message">
                In a normal run, picking a card twice costs 1 heart. You can
                spend a heart to keep playing.
              </p>
              <div
                className="guideHeartDemoOverlay__hearts"
                aria-label="Heart demo"
              >
                <span className="guideHeartDemoOverlay__heartRow">
                  Before: {"❤️".repeat(MAX_HEARTS)}
                </span>
                <span className="guideHeartDemoOverlay__heartRow">
                  After: {"❤️".repeat(MAX_HEARTS - 1)}
                  {"🤍"}
                </span>
              </div>
              <p className="guideHeartDemoOverlay__note">
                This lesson does not take a heart from your pool.
              </p>
              <button
                type="button"
                className="homePrimaryButton guideHeartDemoOverlay__button"
                onClick={this.dismissGuideHeartDemo}
              >
                Got it — keep learning
              </button>
            </div>
          </div>
        )}

        {this.state.showShinyDiscovery && (
          <div
            className="guideHeartDemoOverlay shinyDiscoveryOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="shinyDiscoveryTitle"
          >
            <div className="guideHeartDemoOverlay__panel">
              <div
                id="shinyDiscoveryTitle"
                className="guideHeartDemoOverlay__title"
              >
                {SHINY_DISCOVERY_TITLE}
              </div>
              <p className="guideHeartDemoOverlay__message">
                {SHINY_DISCOVERY_MESSAGE}
              </p>
              <button
                type="button"
                className="homePrimaryButton guideHeartDemoOverlay__button"
                onClick={this.dismissShinyDiscovery}
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {this.state.hiddenLootDiscoveryKind && (
          <div
            className="guideHeartDemoOverlay shinyDiscoveryOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hiddenLootDiscoveryTitle"
          >
            <div className="guideHeartDemoOverlay__panel">
              <div
                id="hiddenLootDiscoveryTitle"
                className="guideHeartDemoOverlay__title"
              >
                {
                  getHiddenLootDiscoveryCopy(
                    this.state.hiddenLootDiscoveryKind
                  ).title
                }
              </div>
              <p className="guideHeartDemoOverlay__message">
                {
                  getHiddenLootDiscoveryCopy(
                    this.state.hiddenLootDiscoveryKind
                  ).message
                }
              </p>
              <button
                type="button"
                className="homePrimaryButton guideHeartDemoOverlay__button"
                onClick={this.dismissHiddenLootDiscovery}
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {this.state.showStoryIntro &&
          isStoryRun(this.getActiveRunConfig()) && (
            <StoryLevelIntro
              level={getStoryLevelById(
                this.getActiveRunConfig().storyLevelId || 1
              )}
              hardMode={isStoryHardModeActive(this.state.storyProgress)}
              onStart={this.dismissStoryIntro}
              onCancel={this.cancelStoryIntro}
            />
          )}

        {this.state.showExitConfirm && (
          <div className="exitConfirmOverlay" role="dialog" aria-modal="true">
            <div className="exitConfirmOverlay__panel">
              <div className="exitConfirmOverlay__title">Leave this run?</div>
              <div className="exitConfirmOverlay__message">
                Progress in this run will be lost. Story levels are only marked
                complete when you finish the board.
              </div>
              <div className="exitConfirmOverlay__actions">
                <button
                  type="button"
                  className="levelCompleteOverlay__button"
                  onClick={this.cancelLeaveRun}
                >
                  Keep Playing
                </button>
                <button
                  type="button"
                  className="levelCompleteOverlay__button levelCompleteOverlay__button--danger"
                  onClick={this.confirmLeaveRun}
                >
                  Leave Run
                </button>
              </div>
            </div>
          </div>
        )}

        {this.state.showResumeOverlay &&
        this.state.pauseReason === "help" ? (
          <ModifierHelpOverlay
            items={getActiveModifierHelpItems(this.state)}
            onResume={this.resumeGame}
          />
        ) : null}

        {this.state.showResumeOverlay &&
        this.state.pauseReason !== "help" ? (
          <div
            className={`resumeOverlay${
              this.state.pauseReason === "manual"
                ? " resumeOverlay--manual"
                : this.state.pauseReason === "screenshot"
                  ? " resumeOverlay--screenshot"
                  : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="resumeOverlayTitle"
          >
            <div className="resumeOverlay__panel">
              <div id="resumeOverlayTitle" className="resumeOverlay__title">
                {this.state.pauseReason === "manual"
                  ? "Paused"
                  : this.state.pauseReason === "screenshot"
                    ? "Camera shy!"
                    : "Welcome back"}
              </div>
              <div className="resumeOverlay__message">
                {this.state.pauseReason === "manual"
                  ? "Your run is paused. Tap resume when you are ready."
                  : this.state.pauseReason === "screenshot"
                    ? "The Corgis don't like to be photographed. They are now Shy and Anxious."
                    : "Your run is paused. Tap continue when you are ready."}
              </div>
              <button
                type="button"
                className="homePrimaryButton resumeOverlay__button"
                onClick={this.resumeGame}
              >
                {this.state.pauseReason === "screenshot" ||
                this.state.pauseReason === "manual"
                  ? "Resume"
                  : "Continue"}
              </button>
            </div>
          </div>
        ) : null}

        {this.state.showLevelOverlay && (
          <div className="levelOverlay" role="status" aria-live="polite">
            <div className="levelOverlay__panel">
              <div className="levelOverlay__label">Level</div>
              <div className="levelOverlay__value">
                {this.state.currentLevel + 1}
              </div>
            </div>
          </div>
        )}

        {this.renderPageMenu()}

        {this.renderAdminPanel()}

        <div id="main-content" className={`container${this.state.showResumeOverlay ? " container--paused" : ""}`}>
          <div className="gameContent">
            <div className="gamePlayStack">
            <div
              className={`gameStats${
                this.shouldCoverGameBarForStoryPrompt()
                  ? " gameStats--coveredByPrompt"
                  : ""
              }`}
              aria-label="Game stats"
            >
              {(this.state.runMode === "blitz" ||
                !isStoryRun(this.getActiveRunConfig())) && (
                <div
                  className={`gameStats__timer${
                    this.state.runMode === "blitz"
                      ? " gameStats__timer--blitz"
                      : ""
                  }`}
                  aria-label={
                    this.state.runMode === "blitz" ? "Blitz timer" : "Run timer"
                  }
                >
                  {this.state.runMode === "blitz"
                    ? formatRunDuration(this.getBlitzRemainingMs())
                    : formatRunDuration(this.state.runElapsedMs)}
                </div>
              )}

              <div className="gameStats__primary">
                {this.state.runStartedAt && !this.state.runPausedAt ? (
                  <button
                    type="button"
                    className="gameStats__pause"
                    onClick={this.togglePauseGame}
                    aria-label="Pause"
                    title="Pause"
                  >
                    <svg
                      className="gameStats__pauseIcon"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <rect x="6" y="5" width="4" height="14" rx="1" />
                      <rect x="14" y="5" width="4" height="14" rx="1" />
                    </svg>
                  </button>
                ) : null}

                <div className="gameStats__center">
                {isStoryRun(this.getActiveRunConfig()) ? (
                  <span className="gameStats__item gameStats__item--emphasis">
                    Story Lvl {this.getActiveRunConfig().storyLevelId || 1}
                    {isStoryHardModeActive(this.state.storyProgress)
                      ? " (Hard)"
                      : ""}
                  </span>
                ) : (
                  <span className="gameStats__item gameStats__item--emphasis">
                    Lv {this.state.currentLevel + 1}
                  </span>
                )}

                {isStoryRun(this.getActiveRunConfig()) && (
                  <span className="gameStats__item gameStats__item--emphasis">
                    {this.getActiveRunConfig().requirePerfectClears
                      ? "Perfect "
                      : ""}
                    Stages {this.state.storyClearsCompleted || 0}/
                    {getStoryClearTarget(this.getActiveRunConfig()) || 1}
                  </span>
                )}

                {isStoryRun(this.getActiveRunConfig()) && (
                  <span className="gameStats__item">
                    Board {this.getStoryBoardPickCount()}/
                    {this.getActiveRunConfig().boardSize ||
                      this.state.clicked.length ||
                      0}
                  </span>
                )}

                {!isStoryRun(this.getActiveRunConfig()) && (
                  <span
                    className="gameStats__item"
                    title="Weighted score for personal bests and leaderboards"
                  >
                    Score {this.state.score}
                    <span className="gameStats__itemHint">
                      {" "}
                      (+
                      {getEndlessPointsPerClick(
                        this.getActiveRunConfig().boardSize,
                        buildEndlessScoreModifiersFromRunConfig(
                          this.getActiveRunConfig()
                        )
                      )}
                      /pick)
                    </span>
                  </span>
                )}

                {!isStoryRun(this.getActiveRunConfig()) &&
                  this.isHighScoreEnabled() && (
                  <span
                    className="gameStats__item"
                    title="Your best Endless score"
                  >
                    Best {this.state.topScore}
                  </span>
                )}

                {!isStoryRun(this.getActiveRunConfig()) && (
                  <span
                    className="gameStats__item"
                    title="Correct picks toward the next level (separate from Score)"
                  >
                    Picks {this.state.endlessProgressClicks || 0}/
                    {this.getLevelConfig(this.state.currentLevel).threshold}
                  </span>
                )}

                {this.state.hardcoreMode || this.state.runMode === "blitz" ? (
                  <span
                    className="gameStats__item gameStats__hearts gameStats__hearts--hardcore"
                    title="Hardcore / Blitz — hearts cannot save a mistake"
                  >
                    No hearts
                  </span>
                ) : (
                  <span className="gameStats__item gameStats__hearts">
                    {formatHeartsMeter(this.state.hearts)}
                    {this.state.hearts < MAX_HEARTS
                      ? ` (${this.getNextHeartMinutesText()})`
                      : ""}
                  </span>
                )}
                </div>

                {this.state.runStartedAt && !this.state.runPausedAt ? (
                  <button
                    type="button"
                    className="gameStats__pause gameStats__help gameStats__help--end"
                    onClick={this.openModifierHelp}
                    aria-label="Run help"
                    title="Run help"
                  >
                    <span className="gameStats__helpIcon" aria-hidden="true">
                      ?
                    </span>
                  </button>
                ) : null}
              </div>

              {(this.state.hardcoreMode ||
                this.state.twistedMode ||
                this.state.shyCorgisMode ||
                this.state.anxiousMode ||
                this.state.runMode === "blitz") && (
                <div className="gameStats__modifiers" aria-label="Active modifiers">
                  {this.state.runMode === "blitz" && (
                    <span className="gameStatsChip gameStatsChip--blitz">
                      Blitz
                    </span>
                  )}
                  {this.state.hardcoreMode && (
                    <span className="gameStatsChip gameStatsChip--hardcore">
                      Hardcore
                    </span>
                  )}
                  {this.state.twistedMode && (
                    <span className="gameStatsChip gameStatsChip--twisted">
                      Twisted
                    </span>
                  )}
                  {this.state.shyCorgisMode && (
                    <span className="gameStatsChip gameStatsChip--shy">
                      Shy
                    </span>
                  )}
                  {this.state.anxiousMode && (
                    <span className="gameStatsChip gameStatsChip--anxious">
                      Anxious
                    </span>
                  )}
                </div>
              )}

              {isStoryBoostFeatureUnlocked(this.state.storyProgress) ? (
                <GameBoostBar
                  entries={listBoostBarEntries({
                    state: this.state,
                    inventory: this.state.inventory,
                    isGuidedRun: this.isGuidedStoryRun(this.getActiveRunConfig()),
                    unusedCardCount: this.getUnusedStoryCardIds().length,
                  })}
                  boostMessage={this.state.boostMessage}
                  onActivate={this.activateBoost}
                />
              ) : null}
            </div>

            {this.renderUnlockToast("aboveBoard")}

            <div
              key={`${this.state.currentLevel}-${this.state.storyBoardDealKey || 0}`}
              className={`gameBoard gameBoard--levelEnter ${
                this.state.runBoostRevealHighlightId
                  ? "gameBoard--boostRevealActive"
                  : ""
              } ${
                this.state.showLevelCompleteOverlay ||
                this.state.showHeartContinueOverlay ||
                this.state.showHeartEmptyOverlay ||
                this.state.showExitConfirm ||
                this.state.showStoryIntro ||
                this.state.showGuideHeartDemo ||
                this.state.showShinyDiscovery ||
                this.state.hiddenLootDiscoveryKind
                  ? "gameBoard--backgroundBlur"
                  : ""
              }`}
            >
              {this.state.cards.map((card, index) => (
                <Row
                  {...card}
                  handleClick={this.handleClick}
                  index={index}
                  levelKey={`${this.state.currentLevel}-${
                    this.state.storyBoardDealKey || 0
                  }`}
                  guideHighlighted={
                    (this.isGuidedStoryRun() &&
                      String(normalizeId(card.id)) ===
                        String(this.state.storyGuideHighlightId || "")) ||
                    String(normalizeId(card.id)) ===
                      String(this.state.runBoostRevealHighlightId || "")
                  }
                  boostRevealHighlighted={
                    String(normalizeId(card.id)) ===
                    String(this.state.runBoostRevealHighlightId || "")
                  }
                  guideDimmed={
                    (this.isGuidedStoryRun() &&
                      Boolean(this.state.storyGuideStep) &&
                      !this.state.showGuideHeartDemo &&
                      String(normalizeId(card.id)) !==
                        String(this.state.storyGuideHighlightId || "")) ||
                    (Boolean(this.state.runBoostRevealHighlightId) &&
                      String(normalizeId(card.id)) !==
                        String(this.state.runBoostRevealHighlightId || ""))
                  }
                  shyCorgisMode={
                    this.state.shyCorgisMode &&
                    !this.state.showStoryIntro &&
                    !this.state.showResumeOverlay &&
                    !this.state.showShinyDiscovery &&
                    !this.state.hiddenLootDiscoveryKind &&
                    !this.state.showGuideHeartDemo
                  }
                  anxiousWarning={this.state.anxiousWarning}
                  shyStartDelayMs={this.getShyStartDelayMs()}
                  requestShyCorgiHide={this.requestShyCorgiHide}
                  releaseShyCorgiHide={this.releaseShyCorgiHide}
                  key={card.id}
                />
              ))}
            </div>
            </div>
          </div>
        </div>

        <BottomNav className="bottomNav">
          {this.renderAppFooter()}
        </BottomNav>
      </>
    );
  }
}

export default App;